import { StaffAccessPanel } from '@/components/StaffAccessPanel';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  Settings, School, Users, Shield, Link, Plus, Pencil, Trash2, 
  Copy, ExternalLink, Check, UserPlus, Baby, Eye, FileText,
  Phone, Mail, MapPin, Briefcase, AlertTriangle, Heart, Download, Clock,
  Building2, Upload, Image, X, ClipboardList, CheckCircle, XCircle, DollarSign,
  CreditCard, ArrowUpRight, RefreshCw, Bell, Send, Loader2, CheckCircle2, HelpCircle, Globe, Gauge
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import EmailTemplatesSettings from '@/components/EmailTemplatesSettings';
import NewsletterPosterSettings from '@/components/NewsletterPosterSettings';
import TransportationTrainingForm from '@/components/TransportationTrainingForm';

import API from '@/config/api';

// Reminders Settings Component
const RemindersSettings = ({ getAuthHeaders }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    staff_reminder_frequency_days: 7,
    new_hire_reminder_days: 3,
    admin_weekly_summary: true
  });
  const [recentLogs, setRecentLogs] = useState([]);
  
  // Enrollment reminder settings
  const [enrollmentSettings, setEnrollmentSettings] = useState({
    enabled: true,
    first_reminder_days: 3,
    second_reminder_days: 5,
    max_reminders: 2
  });
  const [savingEnrollment, setSavingEnrollment] = useState(false);

  // Health report reminders
  const [healthReminders, setHealthReminders] = useState([]);
  const [checkingHealth, setCheckingHealth] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [settingsRes, logsRes, enrollmentRes, healthRes] = await Promise.all([
        axios.get(`${API}/staff/form-reminder-settings`, getAuthHeaders()),
        axios.get(`${API}/staff/form-reminder-logs?limit=10`, getAuthHeaders()),
        axios.get(`${API}/settings/enrollment-reminders`, getAuthHeaders()).catch(() => ({ data: {} })),
        axios.get(`${API}/health-report-reminders`, getAuthHeaders()).catch(() => ({ data: { reminders: [] } }))
      ]);
      setSettings(settingsRes.data);
      setRecentLogs(logsRes.data.logs || []);
      setHealthReminders(healthRes.data.reminders || []);
      if (enrollmentRes.data) {
        setEnrollmentSettings({
          enabled: enrollmentRes.data.enabled ?? true,
          first_reminder_days: enrollmentRes.data.first_reminder_days ?? 3,
          second_reminder_days: enrollmentRes.data.second_reminder_days ?? 5,
          max_reminders: enrollmentRes.data.max_reminders ?? 2
        });
      }
    } catch (error) {
      console.error('Error loading reminder settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const runHealthCheck = async () => {
    setCheckingHealth(true);
    try {
      await axios.post(`${API}/health-report-reminders/check-now`, {}, getAuthHeaders());
      toast.success('Health report compliance check completed');
      const res = await axios.get(`${API}/health-report-reminders`, getAuthHeaders());
      setHealthReminders(res.data.reminders || []);
    } catch (error) {
      toast.error('Failed to run compliance check');
    } finally {
      setCheckingHealth(false);
    }
  };

  const dismissHealthReminder = async (id) => {
    try {
      await axios.post(`${API}/health-report-reminders/${id}/dismiss`, {}, getAuthHeaders());
      setHealthReminders(prev => prev.filter(r => r.id !== id));
      toast.success('Reminder dismissed');
    } catch (error) {
      toast.error('Failed to dismiss reminder');
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/staff/form-reminder-settings`, settings, getAuthHeaders());
      toast.success('Reminder settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const sendRemindersNow = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/staff/send-form-reminders`, {}, getAuthHeaders());
      const result = response.data.result;
      toast.success(`Reminders sent! ${result?.reminders_sent || 0} emails sent to staff with incomplete forms.`);
      loadSettings(); // Reload logs
    } catch (error) {
      toast.error('Failed to send reminders');
    } finally {
      setSendingReminders(false);
    }
  };

  const saveEnrollmentSettings = async () => {
    setSavingEnrollment(true);
    try {
      await axios.put(`${API}/settings/enrollment-reminders`, enrollmentSettings, getAuthHeaders());
      toast.success('Enrollment reminder settings saved');
    } catch (error) {
      toast.error('Failed to save enrollment settings');
    } finally {
      setSavingEnrollment(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-clay">
        <CardContent className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
      {/* Form Reminder Settings */}
      <Card className="card-clay">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Form Compliance Reminders
          </CardTitle>
          <CardDescription>
            Automatically remind staff about incomplete DCF forms
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="font-medium">Enable Automatic Reminders</p>
              <p className="text-sm text-muted-foreground">Send email reminders to staff with incomplete forms</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
            />
          </div>

          {/* Staff Reminder Frequency */}
          <div className="space-y-2">
            <Label>Staff Reminder Frequency</Label>
            <Select
              value={String(settings.staff_reminder_frequency_days)}
              onValueChange={(value) => setSettings({ ...settings, staff_reminder_frequency_days: parseInt(value) })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">Every 3 days</SelectItem>
                <SelectItem value="7">Every 7 days (Weekly)</SelectItem>
                <SelectItem value="14">Every 14 days (Bi-weekly)</SelectItem>
                <SelectItem value="30">Every 30 days (Monthly)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">How often to remind existing staff about incomplete forms</p>
          </div>

          {/* New Hire Reminder Frequency */}
          <div className="space-y-2">
            <Label>New Hire Reminder Frequency</Label>
            <Select
              value={String(settings.new_hire_reminder_days)}
              onValueChange={(value) => setSettings({ ...settings, new_hire_reminder_days: parseInt(value) })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select frequency" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Daily</SelectItem>
                <SelectItem value="2">Every 2 days</SelectItem>
                <SelectItem value="3">Every 3 days</SelectItem>
                <SelectItem value="5">Every 5 days</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">How often to remind new hires (first 7 days) about orientation</p>
          </div>

          {/* Admin Weekly Summary */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="font-medium">Weekly Admin Summary</p>
              <p className="text-sm text-muted-foreground">Send compliance report to admins every Monday</p>
            </div>
            <Switch
              checked={settings.admin_weekly_summary}
              onCheckedChange={(checked) => setSettings({ ...settings, admin_weekly_summary: checked })}
            />
          </div>

          {/* Save Button */}
          <div className="flex gap-2 pt-4">
            <Button onClick={saveSettings} disabled={saving} className="flex-1">
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
            <Button 
              variant="outline" 
              onClick={sendRemindersNow} 
              disabled={sendingReminders || !settings.enabled}
            >
              {sendingReminders ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Reminder Logs */}
      <Card className="card-clay">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Recent Reminders Sent
          </CardTitle>
          <CardDescription>
            Last 10 form reminder emails sent
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentLogs.length > 0 ? (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {recentLogs.map((log, idx) => (
                <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{log.staff_name}</p>
                      <p className="text-xs text-muted-foreground">{log.staff_email}</p>
                    </div>
                    {log.is_new_hire && (
                      <Badge variant="secondary" className="text-xs">New Hire</Badge>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {log.forms_reminded?.map((form, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{form}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(log.sent_at).toLocaleDateString()} at {new Date(log.sent_at).toLocaleTimeString()}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No reminders sent yet</p>
              <p className="text-sm">Reminders are sent daily at 9:00 AM</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Enrollment Reminder Settings */}
      <Card className="card-clay lg:col-span-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Baby className="w-5 h-5 text-purple-500" />
            Parent Enrollment Reminders
          </CardTitle>
          <CardDescription>
            Automatically remind parents who haven't completed enrollment forms
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Enable Enrollment Reminders</p>
                <p className="text-sm text-muted-foreground">Send email reminders to parents with incomplete forms</p>
              </div>
              <Switch
                checked={enrollmentSettings.enabled}
                onCheckedChange={(checked) => setEnrollmentSettings({ ...enrollmentSettings, enabled: checked })}
              />
            </div>

            {/* Max Reminders */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="font-medium">Maximum Reminders</p>
                <p className="text-sm text-muted-foreground">Total reminders sent per enrollment link</p>
              </div>
              <Select
                value={String(enrollmentSettings.max_reminders)}
                onValueChange={(value) => setEnrollmentSettings({ ...enrollmentSettings, max_reminders: parseInt(value) })}
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="4">4</SelectItem>
                  <SelectItem value="5">5</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* First Reminder */}
            <div className="space-y-2">
              <Label>First Reminder After</Label>
              <Select
                value={String(enrollmentSettings.first_reminder_days)}
                onValueChange={(value) => setEnrollmentSettings({ ...enrollmentSettings, first_reminder_days: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="2">2 days</SelectItem>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="5">5 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Days after sending link before first reminder</p>
            </div>

            {/* Second Reminder */}
            <div className="space-y-2">
              <Label>Second Reminder After</Label>
              <Select
                value={String(enrollmentSettings.second_reminder_days)}
                onValueChange={(value) => setEnrollmentSettings({ ...enrollmentSettings, second_reminder_days: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="5">5 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="10">10 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Days after sending link before second reminder</p>
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-6 pt-4 border-t">
            <Button onClick={saveEnrollmentSettings} disabled={savingEnrollment}>
              {savingEnrollment ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save Enrollment Reminder Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Child Health Report Reminders (DCF-F-CFS0060) */}
      <Card className="card-clay lg:col-span-2" data-testid="health-report-settings-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-blue-500" />
                Child Health Report Reminders
              </CardTitle>
              <CardDescription>
                DCF-F-CFS0060 compliance tracking — automatically monitors health report deadlines
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={runHealthCheck}
              disabled={checkingHealth}
              data-testid="settings-run-health-check-btn"
            >
              {checkingHealth ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              {checkingHealth ? 'Checking...' : 'Run Check Now'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* DCF Schedule Rules */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5">
            <p className="font-semibold text-sm text-blue-800 mb-2">DCF Health Report Schedule (Form DCF-F-CFS0060):</p>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start gap-2">
                <Baby className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-blue-900">Birth — 2 years old</p>
                  <p className="text-blue-700">Required at admission (within 3 months), then updated every <span className="font-semibold">6 months</span></p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Baby className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-blue-900">Ages 2 — 5 years old</p>
                  <p className="text-blue-700">Required at admission (within 3 months), then every <span className="font-semibold">2 years</span></p>
                </div>
              </div>
            </div>
          </div>

          {/* Active Reminders */}
          {healthReminders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500 opacity-70" />
              <p className="font-medium">All health reports are up to date</p>
              <p className="text-sm">No children currently need health report updates</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-medium text-muted-foreground">{healthReminders.length} active reminder{healthReminders.length !== 1 ? 's' : ''}</span>
                {healthReminders.filter(r => r.urgency === 'overdue').length > 0 && (
                  <Badge variant="destructive" className="text-xs">
                    {healthReminders.filter(r => r.urgency === 'overdue').length} Overdue
                  </Badge>
                )}
                {healthReminders.filter(r => r.urgency === 'warning').length > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 text-xs border-amber-200">
                    {healthReminders.filter(r => r.urgency === 'warning').length} Due Soon
                  </Badge>
                )}
              </div>
              {healthReminders.map((reminder) => (
                <div
                  key={reminder.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    reminder.urgency === 'overdue' ? 'bg-red-50 border-red-200' :
                    reminder.urgency === 'warning' ? 'bg-amber-50 border-amber-200' :
                    'bg-blue-50 border-blue-200'
                  }`}
                  data-testid={`settings-health-reminder-${reminder.id}`}
                >
                  <div className="flex items-center gap-3">
                    {reminder.urgency === 'overdue' ? (
                      <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
                    ) : reminder.urgency === 'warning' ? (
                      <Clock className="w-5 h-5 text-amber-500 shrink-0" />
                    ) : (
                      <Heart className="w-5 h-5 text-blue-500 shrink-0" />
                    )}
                    <div>
                      <p className="font-semibold text-sm">{reminder.child_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Age: {reminder.age_label} | {reminder.schedule}
                        {reminder.last_report_date && ` | Last: ${reminder.last_report_date}`}
                        {reminder.enrollment_date && ` | Enrolled: ${new Date(reminder.enrollment_date + 'T12:00:00').toLocaleDateString()}`}
                      </p>
                      <p className="text-xs mt-0.5">{reminder.reason}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => dismissHealthReminder(reminder.id)}
                    title="Dismiss reminder"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 pt-4 border-t text-xs text-muted-foreground">
            <p>Health report compliance is checked automatically every day at 8:00 AM. Reminders auto-resolve when the child's health report form is completed.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Payment Settings Component
const PaymentSettings = ({ getAuthHeaders }) => {
  const [stripeStatus, setStripeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchStripeStatus();
  }, []);

  const fetchStripeStatus = async () => {
    try {
      const res = await axios.get(`${API}/payments/connect/status`, getAuthHeaders());
      setStripeStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch Stripe status:', error);
      setStripeStatus({ connected: false, status: 'not_connected' });
    } finally {
      setLoading(false);
    }
  };

  const connectStripe = async () => {
    setConnecting(true);
    try {
      const res = await axios.post(
        `${API}/payments/connect/create-account`,
        {
          return_url: `${window.location.origin}/settings?tab=payments&stripe=success`,
          refresh_url: `${window.location.origin}/settings?tab=payments&stripe=refresh`
        },
        getAuthHeaders()
      );
      
      // Redirect to Stripe onboarding
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Stripe');
      setConnecting(false);
    }
  };

  const disconnectStripe = async () => {
    if (!window.confirm('Are you sure you want to disconnect Stripe? Parents will not be able to make online payments.')) {
      return;
    }
    
    try {
      await axios.post(`${API}/payments/connect/disconnect`, {}, getAuthHeaders());
      toast.success('Stripe disconnected');
      fetchStripeStatus();
    } catch (error) {
      toast.error('Failed to disconnect Stripe');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stripe Connect Card */}
      <Card className="overflow-hidden border-0 shadow-lg">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <CreditCard className="w-6 h-6" />
            Accept Parent Payments
          </h3>
          <p className="text-white/80 text-sm mt-1">
            Connect your Stripe account to accept online payments from parents
          </p>
        </div>
        <CardContent className="p-6">
          {stripeStatus?.connected ? (
            <div className="space-y-4">
              {/* Connected Status */}
              <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-green-800">Stripe Connected</p>
                  <p className="text-sm text-green-600">
                    {stripeStatus.charges_enabled 
                      ? 'Your account is ready to accept payments' 
                      : 'Complete Stripe onboarding to accept payments'}
                  </p>
                </div>
                <Badge className={stripeStatus.charges_enabled ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                  {stripeStatus.charges_enabled ? 'Active' : 'Pending'}
                </Badge>
              </div>

              {/* Account Details */}
              <div className="grid gap-4 md:grid-cols-2">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Business Name</p>
                  <p className="font-medium">{stripeStatus.business_name || 'Not set'}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{stripeStatus.email || 'Not set'}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                {!stripeStatus.charges_enabled && (
                  <Button onClick={connectStripe} disabled={connecting}>
                    Complete Setup
                  </Button>
                )}
                <Button variant="outline" onClick={() => window.open('https://dashboard.stripe.com', '_blank')}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Stripe Dashboard
                </Button>
                <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={disconnectStripe}>
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-indigo-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-indigo-600" />
              </div>
              <h4 className="font-semibold text-lg mb-2">Connect Your Stripe Account</h4>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Connect your Stripe account to allow parents to pay invoices online. 
                Payments go directly to your bank account.
              </p>
              <Button 
                size="lg" 
                onClick={connectStripe} 
                disabled={connecting}
                className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Connect Stripe Account
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                Free to connect. Standard Stripe fees (2.9% + $0.30) apply per transaction.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="card-clay">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-500" />
            How Parent Payments Work
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 bg-muted/50 rounded-lg text-center">
              <div className="w-10 h-10 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">1</div>
              <p className="font-medium mb-1">Create Invoices</p>
              <p className="text-sm text-muted-foreground">Create invoices for tuition, fees, or registration</p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg text-center">
              <div className="w-10 h-10 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">2</div>
              <p className="font-medium mb-1">Parents Pay Online</p>
              <p className="text-sm text-muted-foreground">Parents see invoices in their portal and click "Pay Now"</p>
            </div>
            <div className="p-4 bg-muted/50 rounded-lg text-center">
              <div className="w-10 h-10 mx-auto mb-3 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-bold">3</div>
              <p className="font-medium mb-1">Money to Your Bank</p>
              <p className="text-sm text-muted-foreground">Payments go directly to your connected bank account</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

import DocumentUploader from '@/components/DocumentUploader';

// Simple center documents wrapper
const CenterDocuments = ({ getAuthHeaders, centerId }) => {
  return <DocumentUploader entityType="center" entityId={centerId || 'center'} entityName="Center" />;
};

const SettingsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [classrooms, setClassrooms] = useState([]);
  const [staff, setStaff] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classroomDialogOpen, setClassroomDialogOpen] = useState(false);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [applicationDetailOpen, setApplicationDetailOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  
  // Staff Checklist state
  const [dcfChecklistItems, setDcfChecklistItems] = useState([]);
  const [staffChecklistData, setStaffChecklistData] = useState({});
  const [savingChecklist, setSavingChecklist] = useState(null);
  const [checklistDateRange, setChecklistDateRange] = useState({ start: '', end: '' });
  const [selectedStaffForPDF, setSelectedStaffForPDF] = useState([]);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  
  // Center Settings state
  const [centerSettings, setCenterSettings] = useState({
    center_name: 'CareKids WI',
    center_type: 'group', // 'family' (in-home) or 'group' (commercial building) - drives DCF radon retest cadence
    address: '',
    phone: '',
    email: '',
    license_number: '',
    director_name: '',
    logo_url: null
  });
  const [logoUploading, setLogoUploading] = useState(false);
  const [savingCenter, setSavingCenter] = useState(false);
  
  // Fees Settings state
  const [feesSettings, setFeesSettings] = useState({
    infants: { rate_type: 'weekly', amount: '' },
    toddlers: { rate_type: 'weekly', amount: '' },
    preschool: { rate_type: 'weekly', amount: '' },
    school_age: { rate_type: 'weekly', amount: '' },
    registration: { frequency: 'yearly', amount: '' },
    late_fee: { enabled: false, amount: '', grace_period_days: '7', fee_type: 'flat' },
    payment_terms: { due_type: 'period_end', days_after_invoice: 7, day_of_month: 15 }
  });
  const [savingFees, setSavingFees] = useState(false);
  
  // Shift Times state
  const [shiftTimes, setShiftTimes] = useState([]);
  const [shiftTimeDialogOpen, setShiftTimeDialogOpen] = useState(false);
  const [editingShiftTime, setEditingShiftTime] = useState(null);
  const [shiftTimeForm, setShiftTimeForm] = useState({
    name: '',
    type: 'full', // full, half, custom
    start_time: '08:00',
    end_time: '16:00',
    color: '#10B981'
  });
  const [savingShiftTime, setSavingShiftTime] = useState(false);
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  
  const [editingClassroom, setEditingClassroom] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [classroomForm, setClassroomForm] = useState({
    name: '',
    description: '',
    capacity: 20,
    age_range: '',
    lead_teacher_id: ''
  });
  const [permissions, setPermissions] = useState({
    // Children Management
    can_add_edit_children: true,
    can_view_children: true,
    // Waitlist
    can_manage_waitlist: false,
    // Staff
    can_view_staff_profiles: false,
    // Schedules
    can_add_edit_schedules: false,
    can_view_schedules: true,
    // Other Features
    can_use_ratio_calculator: true,
    can_manage_attendance: true,
    can_view_compliance: true,
    can_edit_compliance: false,
    can_view_billing: false,
    can_create_invoices: false,
    // Messaging
    can_message_parents_and_staff: true,
    can_message_staff_only: false,
    can_create_reports: true
  });
  const [copiedLink, setCopiedLink] = useState(null);

  const baseUrl = window.location.origin;
  const centerId = user?.center_id || user?.id || '';
  const employeeAppLink = `${baseUrl}/apply/employee?center=${centerId}`;
  const childAppLink = `${baseUrl}/apply/child?center=${centerId}`;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classroomsRes, staffRes, applicationsRes, centerRes, shiftTimesRes, dcfItemsRes, feesRes, subscriptionRes, usageRes, plansRes] = await Promise.all([
        axios.get(`${API}/classrooms`, getAuthHeaders()),
        axios.get(`${API}/staff`, getAuthHeaders()),
        axios.get(`${API}/applications`, getAuthHeaders()),
        axios.get(`${API}/settings/center`, getAuthHeaders()).catch(() => ({ data: {} })),
        axios.get(`${API}/settings/shift-times`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/staff/dcf-checklist-items`, getAuthHeaders()).catch(() => ({ data: { checklist_items: [] } })),
        axios.get(`${API}/settings/fees`, getAuthHeaders()).catch(() => ({ data: {} })),
        axios.get(`${API}/subscriptions/my-subscription`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API}/subscriptions/usage`, getAuthHeaders()).catch(() => ({ data: null })),
        axios.get(`${API}/subscriptions/plans`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setClassrooms(classroomsRes.data);
      setStaff(staffRes.data);
      setApplications(applicationsRes.data);
      setShiftTimes(shiftTimesRes.data || []);
      setDcfChecklistItems(dcfItemsRes.data?.checklist_items || []);
      setSubscription(subscriptionRes.data);
      setSubscriptionUsage(usageRes.data);
      setPlans(plansRes.data || []);
      
      // Build staff checklist data map
      const checklistMap = {};
      staffRes.data.forEach(s => {
        checklistMap[s.id] = s.dcf_checklist || {};
      });
      setStaffChecklistData(checklistMap);
      
      if (centerRes.data) {
        setCenterSettings({
          center_name: centerRes.data.center_name || 'CareKids WI',
          center_type: centerRes.data.center_type || 'group',
          address: centerRes.data.address || '',
          phone: centerRes.data.phone || '',
          email: centerRes.data.email || '',
          license_number: centerRes.data.license_number || '',
          director_name: centerRes.data.director_name || '',
          logo_url: centerRes.data.logo_url || null
        });
      }
      
      // Load fees settings
      if (feesRes.data && Object.keys(feesRes.data).length > 0) {
        setFeesSettings({
          infants: feesRes.data.infants || { rate_type: 'weekly', amount: '' },
          toddlers: feesRes.data.toddlers || { rate_type: 'weekly', amount: '' },
          preschool: feesRes.data.preschool || { rate_type: 'weekly', amount: '' },
          school_age: feesRes.data.school_age || { rate_type: 'weekly', amount: '' },
          registration: feesRes.data.registration || { frequency: 'yearly', amount: '' },
          late_fee: feesRes.data.late_fee || { enabled: false, amount: '', grace_period_days: '7', fee_type: 'flat' },
          payment_terms: feesRes.data.payment_terms || { due_type: 'period_end', days_after_invoice: 7, day_of_month: 15 }
        });
      }
    } catch (error) {
      console.error('Failed to load settings data');
    } finally {
      setLoading(false);
    }
  };
  
  // Staff Checklist handlers
  const handleChecklistChange = async (staffId, itemId, value) => {
    setSavingChecklist(`${staffId}-${itemId}`);
    
    // Update local state immediately
    setStaffChecklistData(prev => ({
      ...prev,
      [staffId]: {
        ...(prev[staffId] || {}),
        [itemId]: value
      }
    }));
    
    try {
      const updatedChecklist = {
        ...(staffChecklistData[staffId] || {}),
        [itemId]: value
      };
      await axios.put(`${API}/staff/${staffId}/dcf-checklist`, updatedChecklist, getAuthHeaders());
    } catch (error) {
      toast.error('Failed to update checklist');
      // Revert on error
      fetchData();
    } finally {
      setSavingChecklist(null);
    }
  };

  const openPDFDialog = () => {
    // Pre-select all non-compliant staff
    const nonCompliantIds = staff.filter(s => !s.dcf_compliant).map(s => s.id);
    setSelectedStaffForPDF(nonCompliantIds);
    setPdfDialogOpen(true);
  };

  const toggleStaffForPDF = (staffId) => {
    setSelectedStaffForPDF(prev => 
      prev.includes(staffId) 
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  const selectAllForPDF = () => {
    const allIds = staff.map(s => s.id);
    setSelectedStaffForPDF(allIds);
  };

  const deselectAllForPDF = () => {
    setSelectedStaffForPDF([]);
  };

  const downloadStaffChecklistPDF = async () => {
    if (selectedStaffForPDF.length === 0) {
      toast.error('Please select at least one staff member');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/staff/dcf-checklist/pdf`,
        {
          staff_ids: selectedStaffForPDF,
          start_date: checklistDateRange.start || null,
          end_date: checklistDateRange.end || null
        },
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `DCF-F-CFS1675A-E_Staff_Checklist_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('Staff checklist PDF downloaded');
      setPdfDialogOpen(false);
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const markStaffCompliant = async (staffId, isCompliant) => {
    try {
      await axios.put(`${API}/staff/${staffId}/compliance`, { dcf_compliant: isCompliant }, getAuthHeaders());
      toast.success(isCompliant ? 'Staff marked as compliant' : 'Staff removed from compliant list');
      fetchData();
    } catch (error) {
      toast.error('Failed to update compliance status');
    }
  };

  const handleSaveClassroom = async (e) => {
    e.preventDefault();
    try {
      if (editingClassroom) {
        await axios.put(`${API}/classrooms/${editingClassroom.id}`, classroomForm, getAuthHeaders());
        toast.success('Classroom updated');
      } else {
        await axios.post(`${API}/classrooms`, classroomForm, getAuthHeaders());
        toast.success('Classroom created');
      }
      setClassroomDialogOpen(false);
      resetClassroomForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save classroom');
    }
  };

  const handleDeleteClassroom = async (classroomId) => {
    if (!window.confirm('Are you sure you want to delete this classroom?')) return;
    try {
      await axios.delete(`${API}/classrooms/${classroomId}`, getAuthHeaders());
      toast.success('Classroom deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete classroom');
    }
  };

  const handleEditClassroom = (classroom) => {
    setEditingClassroom(classroom);
    setClassroomForm({
      name: classroom.name,
      description: classroom.description || '',
      capacity: classroom.capacity || 20,
      age_range: classroom.age_range || '',
      lead_teacher_id: classroom.lead_teacher_id || ''
    });
    setClassroomDialogOpen(true);
  };

  const resetClassroomForm = () => {
    setEditingClassroom(null);
    setClassroomForm({
      name: '',
      description: '',
      capacity: 20,
      age_range: '',
      lead_teacher_id: ''
    });
  };

  // Fees handlers
  const handleSaveFees = async () => {
    setSavingFees(true);
    try {
      await axios.post(`${API}/settings/fees`, feesSettings, getAuthHeaders());
      toast.success('Fees saved successfully');
    } catch (error) {
      toast.error('Failed to save fees');
    } finally {
      setSavingFees(false);
    }
  };

  const updateFeeCategory = (category, field, value) => {
    setFeesSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  // Shift Time handlers
  const formatTime12 = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return '';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    const hours = Math.floor(mins / 60);
    const remainMins = mins % 60;
    return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
  };

  const handleSaveShiftTime = async (e) => {
    e.preventDefault();
    setSavingShiftTime(true);
    
    try {
      if (editingShiftTime) {
        await axios.put(`${API}/settings/shift-times/${editingShiftTime.id}`, shiftTimeForm, getAuthHeaders());
        toast.success('Shift time updated');
      } else {
        await axios.post(`${API}/settings/shift-times`, shiftTimeForm, getAuthHeaders());
        toast.success('Shift time created');
      }
      setShiftTimeDialogOpen(false);
      setEditingShiftTime(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save shift time');
    } finally {
      setSavingShiftTime(false);
    }
  };

  const handleDeleteShiftTime = async (shiftTimeId) => {
    if (!window.confirm('Delete this shift time?')) return;
    
    try {
      await axios.delete(`${API}/settings/shift-times/${shiftTimeId}`, getAuthHeaders());
      toast.success('Shift time deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete shift time');
    }
  };

  const handleSeedDefaultShiftTimes = async () => {
    try {
      await axios.post(`${API}/settings/shift-times/seed-defaults`, {}, getAuthHeaders());
      toast.success('Default shift times created');
      fetchData();
    } catch (error) {
      toast.error('Failed to create default shift times');
    }
  };

  const handleOpenPermissions = async (staffMember) => {
    setSelectedStaff(staffMember);
    try {
      const res = await axios.get(`${API}/staff/${staffMember.id}/permissions`, getAuthHeaders());
      setPermissions(res.data);
    } catch (error) {
      // Use defaults
    }
    setPermissionsDialogOpen(true);
  };

  const handleSavePermissions = async () => {
    try {
      await axios.put(`${API}/staff/${selectedStaff.id}/permissions`, permissions, getAuthHeaders());
      toast.success('Permissions updated');
      setPermissionsDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const copyToClipboard = (text, linkType) => {
    navigator.clipboard.writeText(text);
    setCopiedLink(linkType);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const viewApplicationDetails = async (appId) => {
    try {
      const res = await axios.get(`${API}/applications/${appId}`, getAuthHeaders());
      setSelectedApplication(res.data);
      setApplicationDetailOpen(true);
    } catch (error) {
      toast.error('Failed to load application details');
    }
  };

  const downloadApplicationPDF = async (appId, appName) => {
    try {
      const response = await axios.get(`${API}/applications/${appId}/pdf`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `application_${appName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const downloadApplicationFile = async (appId, fileType, fileIndex = 0) => {
    try {
      const response = await axios.get(`${API}/applications/${appId}/file/${fileType}?file_index=${fileIndex}`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = fileType === 'resume' ? 'resume.pdf' : `certification_${fileIndex}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download file');
    }
  };

  const downloadAllApplicationsPDF = async () => {
    if (applications.length === 0) {
      toast.error('No applications to download');
      return;
    }
    
    try {
      toast.info('Preparing bulk PDF download...');
      
      const appIds = applications.map(app => app.id);
      const response = await axios.post(`${API}/applications/bulk-pdf`, appIds, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_applications_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${applications.length} applications as ZIP`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download applications');
    }
  };

  const updateApplicationStatus = async (appId, status, note = null) => {
    try {
      const payload = { status };
      if (note) payload.note = note;
      const res = await axios.put(`${API}/applications/${appId}/status`, payload, getAuthHeaders());
      const data = res.data;
      toast.success(data.message || 'Application status updated');
      
      // Show parent temp password if created
      if (data.parent_created && data.parent_temp_password) {
        toast.info(`Parent account created for ${data.parent_email}. Temp password: ${data.parent_temp_password}`, { duration: 20000 });
      }
      
      // Show created children info
      if (data.children_created && data.children_created.length > 0) {
        const names = data.children_created.map(c => c.name).join(', ');
        toast.success(`Enrolled: ${names}`);
      }
      
      // Show staff created info
      if (data.staff_created) {
        toast.success('Staff record created. Go to Staff page to send invite.');
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const addToWaitlist = async (appId) => {
    try {
      const res = await axios.post(`${API}/waitlist/from-application/${appId}`, {}, getAuthHeaders());
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to waitlist');
    }
  };

  // Center Settings Functions
  const saveCenterSettings = async () => {
    setSavingCenter(true);
    try {
      await axios.put(`${API}/settings/center`, {
        center_name: centerSettings.center_name,
        center_type: centerSettings.center_type,
        address: centerSettings.address,
        phone: centerSettings.phone,
        email: centerSettings.email,
        license_number: centerSettings.license_number,
        director_name: centerSettings.director_name
      }, getAuthHeaders());
      toast.success('Center settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSavingCenter(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPG, GIF, or WebP image');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 2MB');
      return;
    }
    
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/settings/logo`, formData, {
        headers: {
          ...getAuthHeaders().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setCenterSettings(prev => ({ ...prev, logo_url: response.data.logo_url }));
      toast.success('Logo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const deleteLogo = async () => {
    if (!confirm('Are you sure you want to remove the logo?')) return;
    
    try {
      await axios.delete(`${API}/settings/logo`, getAuthHeaders());
      setCenterSettings(prev => ({ ...prev, logo_url: null }));
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Settings">
      <div className="space-y-6" data-testid="settings-page">
        <Tabs defaultValue={typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tab') || 'center'} className="space-y-6">
          <TabsList className="bg-muted p-1 rounded-xl flex flex-wrap h-auto w-full justify-start gap-1" data-testid="settings-tabs-list">
            <TabsTrigger value="center" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Building2 className="w-4 h-4 mr-2" />
              Center Info
            </TabsTrigger>
            <TabsTrigger value="subscription" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <CreditCard className="w-4 h-4 mr-2" />
              Subscription
            </TabsTrigger>
            <TabsTrigger value="fees" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <DollarSign className="w-4 h-4 mr-2" />
              Fees
            </TabsTrigger>
            <TabsTrigger value="shifts" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Clock className="w-4 h-4 mr-2" />
              Shift Times
            </TabsTrigger>
            <TabsTrigger value="classrooms" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <School className="w-4 h-4 mr-2" />
              Classrooms
            </TabsTrigger>
            <TabsTrigger value="permissions" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Shield className="w-4 h-4 mr-2" />
              Staff Permissions
            </TabsTrigger>
            <TabsTrigger value="applications" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Link className="w-4 h-4 mr-2" />
              Applications
            </TabsTrigger>
            <TabsTrigger value="reminders" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Bell className="w-4 h-4 mr-2" />
              Reminders
            </TabsTrigger>
            <TabsTrigger value="payments" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <CreditCard className="w-4 h-4 mr-2" />
              Payments
            </TabsTrigger>
            <TabsTrigger value="email-templates" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Mail className="w-4 h-4 mr-2" />
              Email Templates
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-newsletter">
              <FileText className="w-4 h-4 mr-2" />
              Newsletter
            </TabsTrigger>
            <TabsTrigger value="resources" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <FileText className="w-4 h-4 mr-2" />
              Resources
            </TabsTrigger>
            <TabsTrigger value="documents" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Upload className="w-4 h-4 mr-2" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="payment-agreement" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-payment-agreement">
              <DollarSign className="w-4 h-4 mr-2" />
              Payment Agreement
            </TabsTrigger>
            <TabsTrigger value="transport" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm" data-testid="tab-transport">
              <Gauge className="w-4 h-4 mr-2" />
              Transport
            </TabsTrigger>
          </TabsList>

          {/* Center Settings Tab */}
          <TabsContent value="center">
            <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
              {/* Center Information Card */}
              <Card className="card-clay">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-primary" />
                    Center Information
                  </CardTitle>
                  <CardDescription>Your facility details that appear on forms and documents</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Center Name</Label>
                      <Input 
                        value={centerSettings.center_name}
                        onChange={e => setCenterSettings({...centerSettings, center_name: e.target.value})}
                        placeholder="CareKids WI"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Center Type <span className="text-xs text-muted-foreground font-normal">(drives DCF radon retest cadence)</span></Label>
                      <select
                        value={centerSettings.center_type}
                        onChange={e => setCenterSettings({...centerSettings, center_type: e.target.value})}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                        data-testid="center-type-select"
                      >
                        <option value="group">Group Child Care (commercial building — radon retest every 5 years)</option>
                        <option value="family">Family Child Care (in-home — radon retest every 2 years)</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <Label>Address</Label>
                      <Input 
                        value={centerSettings.address}
                        onChange={e => setCenterSettings({...centerSettings, address: e.target.value})}
                        placeholder="123 Main Street, Madison, WI 53703"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input 
                        value={centerSettings.phone}
                        onChange={e => setCenterSettings({...centerSettings, phone: e.target.value})}
                        placeholder="(608) 555-1234"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        value={centerSettings.email}
                        onChange={e => setCenterSettings({...centerSettings, email: e.target.value})}
                        placeholder="info@carekids.com"
                      />
                    </div>
                    <div>
                      <Label>License/Facility Number</Label>
                      <Input 
                        value={centerSettings.license_number}
                        onChange={e => setCenterSettings({...centerSettings, license_number: e.target.value})}
                        placeholder="DCF-12345"
                      />
                    </div>
                    <div>
                      <Label>Director Name</Label>
                      <Input 
                        value={centerSettings.director_name}
                        onChange={e => setCenterSettings({...centerSettings, director_name: e.target.value})}
                        placeholder="Jane Smith"
                      />
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    <Button onClick={saveCenterSettings} disabled={savingCenter}>
                      {savingCenter ? 'Saving...' : 'Save Center Info'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Logo Upload Card */}
              <Card className="card-clay">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Image className="w-5 h-5 text-primary" />
                    Center Logo
                  </CardTitle>
                  <CardDescription>Upload your logo to display on forms and PDFs</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Current Logo Preview */}
                  <div className="flex items-center justify-center p-6 bg-muted/50 rounded-xl border-2 border-dashed border-muted-foreground/20">
                    {centerSettings.logo_url ? (
                      <div className="text-center">
                        <div className="relative inline-block">
                          <img 
                            src={centerSettings.logo_url?.startsWith('http') ? centerSettings.logo_url : `${API}${centerSettings.logo_url?.replace('/api', '') || ''}`}
                            alt="Center Logo"
                            className="max-w-[200px] max-h-[150px] object-contain rounded-lg shadow-md"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6"
                            onClick={deleteLogo}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <p className="text-sm text-muted-foreground mt-3">Current logo</p>
                      </div>
                    ) : (
                      <div className="text-center">
                        <Image className="w-16 h-16 mx-auto text-muted-foreground/30 mb-3" />
                        <p className="text-sm text-muted-foreground">No logo uploaded</p>
                        <p className="text-xs text-muted-foreground mt-1">Your logo will appear on incident reports and other forms</p>
                      </div>
                    )}
                  </div>

                  {/* Upload Button */}
                  <div className="space-y-2">
                    <Label htmlFor="logo-upload" className="cursor-pointer">
                      <div className="flex items-center justify-center gap-2 p-3 border-2 border-primary/20 rounded-lg hover:bg-primary/5 transition-colors">
                        <Upload className="w-5 h-5 text-primary" />
                        <span className="text-sm font-medium">
                          {logoUploading ? 'Uploading...' : centerSettings.logo_url ? 'Change Logo' : 'Upload Logo'}
                        </span>
                      </div>
                    </Label>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      onChange={handleLogoUpload}
                      disabled={logoUploading}
                      className="hidden"
                    />
                    <p className="text-xs text-muted-foreground text-center">
                      PNG, JPG, GIF or WebP. Max 2MB.
                    </p>
                  </div>

                  {/* Info Box */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-700">
                      <strong>Tip:</strong> For best results, use a square or horizontal logo with a transparent background (PNG). 
                      The logo will appear in the header of incident reports and other official documents.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Staff Record Checklist - DCF-F-CFS1675A-E */}
            <Card className="card-clay mt-6">
              <CardHeader>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="font-heading flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-primary" />
                        DCF-F-CFS1675A-E Staff Record Checklist
                      </CardTitle>
                      <CardDescription>Track compliance requirements for all staff members</CardDescription>
                    </div>
                    <Button variant="outline" onClick={openPDFDialog} data-testid="download-staff-checklist-pdf">
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <span className="text-muted-foreground">
                        Showing: <strong>{staff.filter(s => !s.dcf_compliant).length}</strong> staff requiring review
                      </span>
                      {staff.filter(s => s.dcf_compliant).length > 0 && (
                        <span className="text-green-600">
                          <CheckCircle className="w-4 h-4 inline mr-1" />
                          {staff.filter(s => s.dcf_compliant).length} compliant (hidden)
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {staff.length > 0 && dcfChecklistItems.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="mobile-cards w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-primary/10">
                          <th className="text-left p-2 font-semibold border text-xs w-[120px]">Employee</th>
                          <th className="text-left p-2 font-semibold border text-xs w-[80px]">Position</th>
                          {dcfChecklistItems.map((item) => (
                            <th key={item.id} className="p-1 text-center border w-[55px]">
                              <div className="text-[9px] font-medium leading-tight">{item.label}</div>
                            </th>
                          ))}
                          <th className="p-1 text-center border text-xs w-[65px]">Done</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staff.filter(member => !member.dcf_compliant).map((member) => {
                          const checklist = staffChecklistData[member.id] || {};
                          const completedCount = dcfChecklistItems.filter(item => checklist[item.id] === 'X').length;
                          const allComplete = completedCount === dcfChecklistItems.length;
                          
                          return (
                            <tr key={member.id} className="border hover:bg-muted/30" data-testid={`checklist-row-${member.id}`}>
                              <td className="p-1.5 border bg-white max-w-[120px]">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-bold text-primary flex-shrink-0">
                                    {member.user_name?.charAt(0).toUpperCase() || 'S'}
                                  </div>
                                  <span className="font-medium text-[10px] truncate">{member.user_name}</span>
                                </div>
                              </td>
                              <td className="p-1.5 border text-[9px] text-muted-foreground max-w-[80px] truncate" title={member.position}>
                                {member.position || 'N/A'}
                              </td>
                              {dcfChecklistItems.map((item) => {
                                const value = checklist[item.id] || 'ND';
                                const isSaving = savingChecklist === `${member.id}-${item.id}`;
                                
                                return (
                                  <td key={item.id} className="p-0.5 text-center border">
                                    <Select 
                                      value={value} 
                                      onValueChange={(v) => handleChecklistChange(member.id, item.id, v)}
                                      disabled={isSaving}
                                    >
                                      <SelectTrigger className={`w-10 h-6 text-[9px] mx-auto px-0.5 ${
                                        value === 'X' ? 'bg-green-100 border-green-300' :
                                        value === 'O' ? 'bg-red-100 border-red-300' :
                                        value === 'NA' ? 'bg-gray-100 border-gray-300' :
                                        'bg-yellow-50 border-yellow-300'
                                      }`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="X">X</SelectItem>
                                        <SelectItem value="O">O</SelectItem>
                                        <SelectItem value="ND">ND</SelectItem>
                                        <SelectItem value="NA">NA</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </td>
                                );
                              })}
                              <td className="p-1 text-center border">
                                <Button
                                  variant={allComplete ? "default" : "outline"}
                                  size="sm"
                                  className={`h-6 text-[9px] px-1.5 ${allComplete ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                  onClick={() => markStaffCompliant(member.id, true)}
                                  disabled={!allComplete}
                                  title={allComplete ? "Mark as compliant to hide from list" : "Complete all items first"}
                                  data-testid={`mark-compliant-${member.id}`}
                                >
                                  <CheckCircle className="w-3 h-3" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <ClipboardList className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>{staff.length === 0 ? 'No staff members to display' : 'Loading checklist items...'}</p>
                  </div>
                )}
                
                {/* Show compliant staff in collapsed section */}
                {staff.filter(s => s.dcf_compliant).length > 0 && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-green-800">
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                        Compliant Staff ({staff.filter(s => s.dcf_compliant).length})
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {staff.filter(s => s.dcf_compliant).map(member => (
                        <div key={member.id} className="flex items-center gap-2 bg-white px-2 py-1 rounded border text-xs">
                          <span>{member.user_name}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => markStaffCompliant(member.id, false)}
                            title="Remove from compliant list"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Legend */}
                <div className="mt-4 p-3 bg-muted/50 rounded-lg flex flex-wrap gap-4 text-xs">
                  <span className="font-semibold">Status Codes:</span>
                  <span className="flex items-center gap-1"><span className="w-4 h-4 bg-green-100 border border-green-300 rounded"></span> X = Met</span>
                  <span className="flex items-center gap-1"><span className="w-4 h-4 bg-red-100 border border-red-300 rounded"></span> O = Not Met</span>
                  <span className="flex items-center gap-1"><span className="w-4 h-4 bg-yellow-50 border border-yellow-300 rounded"></span> ND = Not Yet Due</span>
                  <span className="flex items-center gap-1"><span className="w-4 h-4 bg-gray-100 border border-gray-300 rounded"></span> NA = Not Applicable</span>
                </div>
              </CardContent>
            </Card>

            {/* Data Management Card */}
            <Card className="card-clay border-red-200">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  Data Management
                </CardTitle>
                <CardDescription>Clear demo or sample data from your account</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  If your account has demo/sample data (e.g. from initial setup), you can clear it here to start fresh. This removes all children, classrooms, staff records, attendance, and daily reports.
                </p>
                <Button
                  variant="destructive"
                  data-testid="clear-demo-data-btn"
                  onClick={async () => {
                    if (!window.confirm('Are you sure you want to clear ALL children, classrooms, staff, attendance, and daily report data? This cannot be undone.')) return;
                    if (!window.confirm('This is permanent. Type OK to proceed.')) return;
                    try {
                      const res = await axios.post(`${API}/clear-demo-data`, {}, getAuthHeaders());
                      toast.success(res.data.message);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to clear demo data');
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Demo Data
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <div className="grid gap-6 lg:grid-cols-2 [&>*]:min-w-0">
              {/* Current Plan Card */}
              <Card className="card-clay">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    Current Subscription
                  </CardTitle>
                  <CardDescription>Manage your subscription plan and billing</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {subscription ? (
                    <>
                      <div className="p-4 bg-primary/10 rounded-xl border border-primary/20">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <p className="text-sm text-muted-foreground">Current Plan</p>
                            <h3 className="text-2xl font-bold">{subscription.plan_name}</h3>
                          </div>
                          <Badge className={subscription.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                            {subscription.status === 'active' ? 'Active' : subscription.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Monthly Price</p>
                            <p className="font-semibold">${subscription.amount}/mo</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Billing Cycle</p>
                            <p className="font-semibold capitalize">{subscription.interval || 'Monthly'}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Current Period</p>
                            <p className="font-semibold">
                              {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Billing Type</p>
                            <p className="font-semibold capitalize">{subscription.billing_type || 'Recurring'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {subscription.cancel_at_period_end && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div>
                              <p className="font-semibold text-amber-800">Cancellation Scheduled</p>
                              <p className="text-sm text-amber-700">
                                Your subscription will end on {new Date(subscription.current_period_end).toLocaleDateString()}.
                                You can reactivate anytime before this date.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-3">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={async () => {
                            try {
                              setSubscriptionLoading(true);
                              const res = await axios.post(`${API}/subscriptions/customer-portal`, 
                                { origin_url: window.location.origin },
                                getAuthHeaders()
                              );
                              window.open(res.data.portal_url, '_blank');
                            } catch (error) {
                              const message = error.response?.data?.detail || 'Failed to open billing portal';
                              toast.error(message);
                            } finally {
                              setSubscriptionLoading(false);
                            }
                          }}
                          disabled={subscriptionLoading}
                          data-testid="manage-billing-btn"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Manage Billing
                        </Button>
                        
                        {subscription.cancel_at_period_end ? (
                          <Button 
                            className="flex-1"
                            onClick={async () => {
                              try {
                                setSubscriptionLoading(true);
                                await axios.post(`${API}/subscriptions/reactivate`, {}, getAuthHeaders());
                                toast.success('Subscription reactivated!');
                                fetchData();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to reactivate');
                              } finally {
                                setSubscriptionLoading(false);
                              }
                            }}
                            disabled={subscriptionLoading}
                            data-testid="reactivate-btn"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reactivate
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            className="flex-1 text-destructive border-destructive hover:bg-destructive hover:text-white"
                            onClick={() => setCancelDialogOpen(true)}
                            disabled={subscriptionLoading}
                            data-testid="cancel-subscription-btn"
                          >
                            Cancel Subscription
                          </Button>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <CreditCard className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                      <h3 className="font-semibold text-lg mb-2">No Active Subscription</h3>
                      <p className="text-muted-foreground mb-4">Choose a plan to get started with CareKids WI</p>
                      <Button onClick={() => window.location.href = '/pricing'} data-testid="view-plans-btn">
                        View Plans
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Usage Card */}
              {subscriptionUsage?.has_subscription && (
                <Card className="card-clay">
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Usage & Overages
                    </CardTitle>
                    <CardDescription>Your current usage against plan limits</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Children Usage */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Baby className="w-4 h-4 text-primary" />
                          Children
                        </span>
                        <span className="font-semibold">
                          {subscriptionUsage.usage?.children?.current} / {subscriptionUsage.usage?.children?.limit}
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            subscriptionUsage.usage?.children?.current > subscriptionUsage.usage?.children?.limit 
                              ? 'bg-amber-500' 
                              : 'bg-primary'
                          }`}
                          style={{ width: `${Math.min(100, (subscriptionUsage.usage?.children?.current / subscriptionUsage.usage?.children?.limit) * 100)}%` }}
                        />
                      </div>
                      {subscriptionUsage.usage?.children?.overage > 0 && (
                        <p className="text-xs text-amber-600">
                          {subscriptionUsage.usage.children.overage} over limit
                          {subscriptionUsage.usage.children.overage_charge > 0 && ` (+$${subscriptionUsage.usage.children.overage_charge}/mo)`}
                        </p>
                      )}
                    </div>
                    
                    {/* Staff Usage */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-600" />
                          Staff
                        </span>
                        <span className="font-semibold">
                          {subscriptionUsage.usage?.staff?.current} / {subscriptionUsage.usage?.staff?.limit}
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            subscriptionUsage.usage?.staff?.current > subscriptionUsage.usage?.staff?.limit 
                              ? 'bg-amber-500' 
                              : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min(100, (subscriptionUsage.usage?.staff?.current / subscriptionUsage.usage?.staff?.limit) * 100)}%` }}
                        />
                      </div>
                      {subscriptionUsage.usage?.staff?.overage > 0 && (
                        <p className="text-xs text-amber-600">
                          {subscriptionUsage.usage.staff.overage} over limit (+${subscriptionUsage.usage.staff.overage_charge}/mo)
                        </p>
                      )}
                    </div>
                    
                    {/* Billing Summary */}
                    <div className="p-4 bg-muted/50 rounded-xl mt-4">
                      <p className="text-sm text-muted-foreground mb-1">Estimated Monthly Bill</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold">${subscriptionUsage.billing?.estimated_total?.toFixed(2)}</span>
                        {subscriptionUsage.billing?.total_overage > 0 && (
                          <span className="text-sm text-amber-600 flex items-center">
                            <ArrowUpRight className="w-3 h-3" />
                            +${subscriptionUsage.billing.total_overage.toFixed(2)} overage
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-2 space-y-0.5">
                        <p>Base: ${subscriptionUsage.billing?.base_price?.toFixed(2)}/mo</p>
                        {subscriptionUsage.billing?.staff_overage_charge > 0 && (
                          <p>Staff overage: +${subscriptionUsage.billing.staff_overage_charge.toFixed(2)}</p>
                        )}
                        {subscriptionUsage.billing?.children_overage_charge > 0 && (
                          <p>Children overage: +${subscriptionUsage.billing.children_overage_charge.toFixed(2)}</p>
                        )}
                      </div>
                      {subscriptionUsage.billing?.total_overage > 0 && (
                        <p className="text-xs text-muted-foreground/80 mt-2 pt-2 border-t border-border/50">
                          Overages added to next invoice automatically
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Cancel Subscription Dialog */}
            <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cancel Subscription</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to cancel your subscription? You'll still have access until the end of your current billing period.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                    Keep Subscription
                  </Button>
                  <Button 
                    variant="destructive"
                    onClick={async () => {
                      try {
                        setSubscriptionLoading(true);
                        await axios.post(`${API}/subscriptions/cancel`, {}, getAuthHeaders());
                        toast.success('Subscription will be canceled at period end');
                        setCancelDialogOpen(false);
                        fetchData();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
                      } finally {
                        setSubscriptionLoading(false);
                      }
                    }}
                    disabled={subscriptionLoading}
                    data-testid="confirm-cancel-btn"
                  >
                    Yes, Cancel Subscription
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Fees Tab */}
          <TabsContent value="fees">
            <Card className="card-clay">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-primary" />
                  Tuition & Fees
                </CardTitle>
                <CardDescription>Set pricing for different age groups and registration fees</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Age Group Pricing */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Age Group Tuition</h3>
                  
                  {/* Infants */}
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Baby className="w-5 h-5 text-pink-500" />
                      <Label className="font-semibold text-base">Infants (0-12 months)</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Rate Type</Label>
                        <Select 
                          value={feesSettings.infants.rate_type} 
                          onValueChange={(v) => updateFeeCategory('infants', 'rate_type', v)}
                        >
                          <SelectTrigger data-testid="infants-rate-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Amount</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="number"
                            placeholder="0.00"
                            className="pl-8"
                            value={feesSettings.infants.amount}
                            onChange={(e) => updateFeeCategory('infants', 'amount', e.target.value)}
                            data-testid="infants-amount"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Toddlers */}
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Baby className="w-5 h-5 text-blue-500" />
                      <Label className="font-semibold text-base">Toddlers (1-3 years)</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Rate Type</Label>
                        <Select 
                          value={feesSettings.toddlers.rate_type} 
                          onValueChange={(v) => updateFeeCategory('toddlers', 'rate_type', v)}
                        >
                          <SelectTrigger data-testid="toddlers-rate-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Amount</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="number"
                            placeholder="0.00"
                            className="pl-8"
                            value={feesSettings.toddlers.amount}
                            onChange={(e) => updateFeeCategory('toddlers', 'amount', e.target.value)}
                            data-testid="toddlers-amount"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Preschool */}
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2 mb-3">
                      <School className="w-5 h-5 text-green-500" />
                      <Label className="font-semibold text-base">Preschool (3-5 years)</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Rate Type</Label>
                        <Select 
                          value={feesSettings.preschool.rate_type} 
                          onValueChange={(v) => updateFeeCategory('preschool', 'rate_type', v)}
                        >
                          <SelectTrigger data-testid="preschool-rate-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Amount</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="number"
                            placeholder="0.00"
                            className="pl-8"
                            value={feesSettings.preschool.amount}
                            onChange={(e) => updateFeeCategory('preschool', 'amount', e.target.value)}
                            data-testid="preschool-amount"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* School Age */}
                  <div className="p-4 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Users className="w-5 h-5 text-purple-500" />
                      <Label className="font-semibold text-base">School Age (6+ years)</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Rate Type</Label>
                        <Select 
                          value={feesSettings.school_age.rate_type} 
                          onValueChange={(v) => updateFeeCategory('school_age', 'rate_type', v)}
                        >
                          <SelectTrigger data-testid="school-age-rate-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Amount</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="number"
                            placeholder="0.00"
                            className="pl-8"
                            value={feesSettings.school_age.amount}
                            onChange={(e) => updateFeeCategory('school_age', 'amount', e.target.value)}
                            data-testid="school-age-amount"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Registration Fee */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-lg mb-4">Registration Fee</h3>
                  <div className="p-4 border rounded-lg bg-amber-50/50 border-amber-200">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-5 h-5 text-amber-600" />
                      <Label className="font-semibold text-base">Registration Fee</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Frequency</Label>
                        <Select 
                          value={feesSettings.registration.frequency} 
                          onValueChange={(v) => updateFeeCategory('registration', 'frequency', v)}
                        >
                          <SelectTrigger data-testid="registration-frequency">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="yearly">Yearly (1x per year)</SelectItem>
                            <SelectItem value="biannual">Bi-Annual (2x per year)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground">Amount</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            type="number"
                            placeholder="0.00"
                            className="pl-8"
                            value={feesSettings.registration.amount}
                            onChange={(e) => updateFeeCategory('registration', 'amount', e.target.value)}
                            data-testid="registration-amount"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Late Fee */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-lg mb-4">Late Payment Fee</h3>
                  <div className="p-4 border rounded-lg bg-red-50/50 border-red-200">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-red-600" />
                        <Label className="font-semibold text-base">Enable Late Fees</Label>
                      </div>
                      <Switch
                        checked={feesSettings.late_fee?.enabled || false}
                        onCheckedChange={(checked) => updateFeeCategory('late_fee', 'enabled', checked)}
                        data-testid="late-fee-toggle"
                      />
                    </div>
                    
                    {feesSettings.late_fee?.enabled && (
                      <div className="space-y-4 pt-2 border-t border-red-200">
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div>
                            <Label className="text-sm text-muted-foreground">Fee Type</Label>
                            <Select 
                              value={feesSettings.late_fee.fee_type || 'flat'} 
                              onValueChange={(v) => updateFeeCategory('late_fee', 'fee_type', v)}
                            >
                              <SelectTrigger data-testid="late-fee-type">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="flat">Flat Fee</SelectItem>
                                <SelectItem value="percentage">Percentage of Invoice</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm text-muted-foreground">
                              {feesSettings.late_fee.fee_type === 'percentage' ? 'Percentage (%)' : 'Amount ($)'}
                            </Label>
                            <div className="relative">
                              {feesSettings.late_fee.fee_type === 'percentage' ? (
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                              ) : (
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                              )}
                              <Input
                                type="number"
                                placeholder={feesSettings.late_fee.fee_type === 'percentage' ? '5' : '25.00'}
                                className={feesSettings.late_fee.fee_type === 'percentage' ? 'pr-8' : 'pl-8'}
                                value={feesSettings.late_fee.amount}
                                onChange={(e) => updateFeeCategory('late_fee', 'amount', e.target.value)}
                                data-testid="late-fee-amount"
                              />
                            </div>
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm text-muted-foreground">Grace Period (days after due date)</Label>
                          <Input
                            type="number"
                            placeholder="7"
                            min="0"
                            max="30"
                            value={feesSettings.late_fee.grace_period_days}
                            onChange={(e) => updateFeeCategory('late_fee', 'grace_period_days', e.target.value)}
                            data-testid="late-fee-grace-period"
                            className="max-w-[200px]"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Late fee will be applied after this many days past the due date
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Terms */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-lg mb-4">Invoice Payment Terms</h3>
                  <div className="p-4 border rounded-lg bg-blue-50/50 border-blue-200">
                    <div className="flex items-center gap-2 mb-4">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <Label className="font-semibold text-base">When is payment due?</Label>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                      Choose when invoice payments should be due. This applies to invoices generated from the Billing Worksheet.
                    </p>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm text-muted-foreground">Payment Due</Label>
                        <Select 
                          value={feesSettings.payment_terms?.due_type || 'period_end'} 
                          onValueChange={(v) => updateFeeCategory('payment_terms', 'due_type', v)}
                        >
                          <SelectTrigger data-testid="payment-terms-type" className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                            <SelectItem value="days_after">Days After Invoice</SelectItem>
                            <SelectItem value="day_of_month">Specific Day of Month</SelectItem>
                            <SelectItem value="period_end">End of Billing Period</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {feesSettings.payment_terms?.due_type === 'days_after' && (
                        <div>
                          <Label className="text-sm text-muted-foreground">Number of Days After Invoice</Label>
                          <Select 
                            value={String(feesSettings.payment_terms?.days_after_invoice || 7)} 
                            onValueChange={(v) => updateFeeCategory('payment_terms', 'days_after_invoice', parseInt(v))}
                          >
                            <SelectTrigger data-testid="payment-terms-days" className="bg-white max-w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="7">Net 7 (7 days)</SelectItem>
                              <SelectItem value="14">Net 14 (14 days)</SelectItem>
                              <SelectItem value="15">Net 15 (15 days)</SelectItem>
                              <SelectItem value="30">Net 30 (30 days)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground mt-1">
                            Payment will be due this many days after the invoice is created
                          </p>
                        </div>
                      )}
                      
                      {feesSettings.payment_terms?.due_type === 'day_of_month' && (
                        <div>
                          <Label className="text-sm text-muted-foreground">Day of Month</Label>
                          <Select 
                            value={String(feesSettings.payment_terms?.day_of_month || 15)} 
                            onValueChange={(v) => updateFeeCategory('payment_terms', 'day_of_month', parseInt(v))}
                          >
                            <SelectTrigger data-testid="payment-terms-day-of-month" className="bg-white max-w-[200px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1st of the month</SelectItem>
                              <SelectItem value="5">5th of the month</SelectItem>
                              <SelectItem value="10">10th of the month</SelectItem>
                              <SelectItem value="15">15th of the month</SelectItem>
                              <SelectItem value="20">20th of the month</SelectItem>
                              <SelectItem value="25">25th of the month</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground mt-1">
                            Payment will always be due on this day of the following month
                          </p>
                        </div>
                      )}
                      
                      {feesSettings.payment_terms?.due_type === 'period_end' && (
                        <p className="text-xs text-muted-foreground bg-white p-2 rounded border">
                          Payment will be due at the end of the billing period (e.g., end of the week for weekly billing)
                        </p>
                      )}
                      
                      {feesSettings.payment_terms?.due_type === 'due_on_receipt' && (
                        <p className="text-xs text-muted-foreground bg-white p-2 rounded border">
                          Payment is due immediately when the parent receives the invoice
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleSaveFees} disabled={savingFees} data-testid="save-fees-btn">
                    {savingFees ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Save Fees
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shift Times Tab */}
          <TabsContent value="shifts">
            <Card className="card-clay">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    Shift Times
                  </CardTitle>
                  <CardDescription>Define standard shift times for your center (First Shift, Second Shift, Half-time, etc.)</CardDescription>
                </div>
                <Dialog open={shiftTimeDialogOpen} onOpenChange={setShiftTimeDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => {
                      setEditingShiftTime(null);
                      setShiftTimeForm({
                        name: '',
                        type: 'full',
                        start_time: '08:00',
                        end_time: '16:00',
                        color: '#10B981'
                      });
                    }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Shift Time
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingShiftTime ? 'Edit Shift Time' : 'Add Shift Time'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveShiftTime} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Shift Name *</Label>
                        <Input
                          value={shiftTimeForm.name}
                          onChange={(e) => setShiftTimeForm({...shiftTimeForm, name: e.target.value})}
                          placeholder="e.g., First Shift, Morning Half"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Shift Type</Label>
                        <Select 
                          value={shiftTimeForm.type} 
                          onValueChange={(v) => setShiftTimeForm({...shiftTimeForm, type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="full">Full Day</SelectItem>
                            <SelectItem value="half">Half Day</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Start Time *</Label>
                          <Input
                            type="time"
                            value={shiftTimeForm.start_time}
                            onChange={(e) => setShiftTimeForm({...shiftTimeForm, start_time: e.target.value})}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>End Time *</Label>
                          <Input
                            type="time"
                            value={shiftTimeForm.end_time}
                            onChange={(e) => setShiftTimeForm({...shiftTimeForm, end_time: e.target.value})}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Color</Label>
                        <div className="flex gap-2 flex-wrap">
                          {[
                            { value: '#10B981', label: 'Green' },
                            { value: '#3B82F6', label: 'Blue' },
                            { value: '#F59E0B', label: 'Amber' },
                            { value: '#8B5CF6', label: 'Purple' },
                            { value: '#EC4899', label: 'Pink' },
                            { value: '#EF4444', label: 'Red' },
                            { value: '#D4A853', label: 'Gold' },
                          ].map((color) => (
                            <button
                              key={color.value}
                              type="button"
                              className={`w-8 h-8 rounded-full border-2 transition-all ${
                                shiftTimeForm.color === color.value ? 'border-gray-800 scale-110' : 'border-transparent'
                              }`}
                              style={{ backgroundColor: color.value }}
                              onClick={() => setShiftTimeForm({...shiftTimeForm, color: color.value})}
                            />
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setShiftTimeDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={savingShiftTime}>
                          {savingShiftTime ? 'Saving...' : (editingShiftTime ? 'Update' : 'Create')}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {shiftTimes.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg font-medium">No shift times defined</p>
                    <p className="text-sm mb-4">Create standard shift times to use in scheduling</p>
                    <Button variant="outline" onClick={() => handleSeedDefaultShiftTimes()}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Default Shifts
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {shiftTimes.map((shift) => (
                      <div
                        key={shift.id}
                        className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                        style={{ borderLeftColor: shift.color, borderLeftWidth: '4px' }}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-lg">{shift.name}</h4>
                            <Badge variant="outline" className="mt-1">
                              {shift.type === 'full' ? 'Full Day' : shift.type === 'half' ? 'Half Day' : 'Custom'}
                            </Badge>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => {
                                setEditingShiftTime(shift);
                                setShiftTimeForm({
                                  name: shift.name,
                                  type: shift.type || 'full',
                                  start_time: shift.start_time,
                                  end_time: shift.end_time,
                                  color: shift.color || '#10B981'
                                });
                                setShiftTimeDialogOpen(true);
                              }}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteShiftTime(shift.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2 text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span className="font-medium">
                            {formatTime12(shift.start_time)} - {formatTime12(shift.end_time)}
                          </span>
                          <span className="text-xs">({calculateDuration(shift.start_time, shift.end_time)})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {shiftTimes.length > 0 && (
                  <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>Tip:</strong> These shift times will appear as quick-select options when adding shifts to the schedule.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Classrooms Tab */}
          <TabsContent value="classrooms">
            <Card className="card-clay">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <School className="w-5 h-5 text-primary" />
                    Classrooms
                  </CardTitle>
                  <CardDescription>Manage your facility&apos;s classrooms and age groups</CardDescription>
                </div>
                <Dialog open={classroomDialogOpen} onOpenChange={(open) => { setClassroomDialogOpen(open); if (!open) resetClassroomForm(); }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-classroom-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Classroom
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-heading">
                        {editingClassroom ? 'Edit Classroom' : 'Add Classroom'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveClassroom} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label>Classroom Name</Label>
                        <Input
                          value={classroomForm.name}
                          onChange={(e) => setClassroomForm({...classroomForm, name: e.target.value})}
                          placeholder="e.g., Butterfly Room"
                          required
                          data-testid="classroom-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={classroomForm.description}
                          onChange={(e) => setClassroomForm({...classroomForm, description: e.target.value})}
                          placeholder="Brief description of this classroom"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Capacity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={classroomForm.capacity}
                            onChange={(e) => setClassroomForm({...classroomForm, capacity: parseInt(e.target.value)})}
                            data-testid="classroom-capacity-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Age Range</Label>
                          <Input
                            value={classroomForm.age_range}
                            onChange={(e) => setClassroomForm({...classroomForm, age_range: e.target.value})}
                            placeholder="e.g., 2-3 years"
                            data-testid="classroom-age-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Lead Teacher</Label>
                        <Select 
                          value={classroomForm.lead_teacher_id} 
                          onValueChange={(value) => setClassroomForm({...classroomForm, lead_teacher_id: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select lead teacher (optional)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {staff.map((s) => (
                              <SelectItem key={s.user_id} value={s.user_id}>
                                {s.user_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setClassroomDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" data-testid="save-classroom-btn">
                          {editingClassroom ? 'Update' : 'Create'} Classroom
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {classrooms.length > 0 ? (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {classrooms.map((classroom) => (
                      <div 
                        key={classroom.id}
                        className="p-4 bg-muted/50 rounded-xl border border-transparent hover:border-primary/20 transition-colors"
                        data-testid={`classroom-${classroom.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-lg">{classroom.name}</h3>
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => handleEditClassroom(classroom)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteClassroom(classroom.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {classroom.description && (
                          <p className="text-sm text-muted-foreground mb-2">{classroom.description}</p>
                        )}
                        <div className="flex flex-wrap gap-2 text-xs">
                          <Badge variant="secondary">Capacity: {classroom.capacity}</Badge>
                          {classroom.age_range && <Badge variant="secondary">{classroom.age_range}</Badge>}
                          {classroom.lead_teacher_name && (
                            <Badge variant="outline">Lead: {classroom.lead_teacher_name}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <School className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No classrooms created yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Staff Permissions Tab */}
          <TabsContent value="permissions">
            <StaffAccessPanel />
            <Card className="card-clay">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Staff Permissions
                </CardTitle>
                <CardDescription>Configure what each staff member can access</CardDescription>
              </CardHeader>
              <CardContent>
                {staff.length > 0 ? (
                  <div className="space-y-4">
                    {staff.map((member) => (
                      <div 
                        key={member.id}
                        className="flex items-center justify-between p-4 bg-muted/50 rounded-xl"
                        data-testid={`staff-permissions-${member.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="font-bold text-primary">
                              {member.user_name?.charAt(0).toUpperCase() || 'S'}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold">{member.user_name}</p>
                            <p className="text-sm text-muted-foreground">{member.position}</p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => handleOpenPermissions(member)}>
                          <Shield className="w-4 h-4 mr-2" />
                          Manage Permissions
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No staff members to manage</p>
                  </div>
                )}

                {/* Permissions Dialog */}
                <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
                  <DialogContent className="max-w-lg max-h-[85vh]">
                    <DialogHeader>
                      <DialogTitle className="font-heading">
                        Permissions for {selectedStaff?.user_name}
                      </DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="h-[60vh] pr-4">
                      <div className="space-y-6 mt-4">
                        {/* Children Management */}
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">👶 Children Management</h4>
                          <div className="space-y-2">
                            {[
                              { key: 'can_add_edit_children', label: 'Add/Edit Children', desc: 'Create and modify child profiles' },
                              { key: 'can_view_children', label: 'View Only', desc: 'View child profiles without editing' },
                            ].map((perm) => (
                              <div key={perm.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                <div>
                                  <p className="font-medium text-sm">{perm.label}</p>
                                  <p className="text-xs text-muted-foreground">{perm.desc}</p>
                                </div>
                                <Switch
                                  checked={permissions[perm.key]}
                                  onCheckedChange={(checked) => setPermissions({...permissions, [perm.key]: checked})}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Waitlist */}
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">📋 Waitlist</h4>
                          <div className="space-y-2">
                            {[
                              { key: 'can_manage_waitlist', label: 'Manage Waitlist', desc: 'Add, edit, and manage waitlist entries' },
                            ].map((perm) => (
                              <div key={perm.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                <div>
                                  <p className="font-medium text-sm">{perm.label}</p>
                                  <p className="text-xs text-muted-foreground">{perm.desc}</p>
                                </div>
                                <Switch
                                  checked={permissions[perm.key]}
                                  onCheckedChange={(checked) => setPermissions({...permissions, [perm.key]: checked})}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Staff Profiles */}
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">👥 Staff Profiles</h4>
                          <div className="space-y-2">
                            {[
                              { key: 'can_view_staff_profiles', label: 'View Staff Profiles', desc: 'View other staff member profiles' },
                            ].map((perm) => (
                              <div key={perm.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                <div>
                                  <p className="font-medium text-sm">{perm.label}</p>
                                  <p className="text-xs text-muted-foreground">{perm.desc}</p>
                                </div>
                                <Switch
                                  checked={permissions[perm.key]}
                                  onCheckedChange={(checked) => setPermissions({...permissions, [perm.key]: checked})}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Schedules */}
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">📅 Schedules</h4>
                          <div className="space-y-2">
                            {[
                              { key: 'can_add_edit_schedules', label: 'Add/Edit Schedules', desc: 'Create and modify staff schedules' },
                              { key: 'can_view_schedules', label: 'View Only', desc: 'View schedules without editing' },
                            ].map((perm) => (
                              <div key={perm.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                <div>
                                  <p className="font-medium text-sm">{perm.label}</p>
                                  <p className="text-xs text-muted-foreground">{perm.desc}</p>
                                </div>
                                <Switch
                                  checked={permissions[perm.key]}
                                  onCheckedChange={(checked) => setPermissions({...permissions, [perm.key]: checked})}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Operations */}
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">⚙️ Operations</h4>
                          <div className="space-y-2">
                            {[
                              { key: 'can_use_ratio_calculator', label: 'Ratio Calculator', desc: 'Access staff-to-child ratio worksheet' },
                              { key: 'can_manage_attendance', label: 'Manage Attendance', desc: 'Check children in and out' },
                            ].map((perm) => (
                              <div key={perm.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                <div>
                                  <p className="font-medium text-sm">{perm.label}</p>
                                  <p className="text-xs text-muted-foreground">{perm.desc}</p>
                                </div>
                                <Switch
                                  checked={permissions[perm.key]}
                                  onCheckedChange={(checked) => setPermissions({...permissions, [perm.key]: checked})}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Compliance */}
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">🛡️ Compliance</h4>
                          <div className="space-y-2">
                            {[
                              { key: 'can_view_compliance', label: 'View Compliance', desc: 'View DCF compliance checklist' },
                              { key: 'can_edit_compliance', label: 'Edit Compliance', desc: 'Mark compliance items complete' },
                            ].map((perm) => (
                              <div key={perm.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                <div>
                                  <p className="font-medium text-sm">{perm.label}</p>
                                  <p className="text-xs text-muted-foreground">{perm.desc}</p>
                                </div>
                                <Switch
                                  checked={permissions[perm.key]}
                                  onCheckedChange={(checked) => setPermissions({...permissions, [perm.key]: checked})}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Billing */}
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">💰 Billing</h4>
                          <div className="space-y-2">
                            {[
                              { key: 'can_view_billing', label: 'View Billing', desc: 'View invoices and payment history' },
                              { key: 'can_create_invoices', label: 'Create Invoices', desc: 'Generate new invoices' },
                            ].map((perm) => (
                              <div key={perm.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                <div>
                                  <p className="font-medium text-sm">{perm.label}</p>
                                  <p className="text-xs text-muted-foreground">{perm.desc}</p>
                                </div>
                                <Switch
                                  checked={permissions[perm.key]}
                                  onCheckedChange={(checked) => setPermissions({...permissions, [perm.key]: checked})}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Messaging */}
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">💬 Messaging</h4>
                          <div className="space-y-2">
                            {[
                              { key: 'can_message_parents_and_staff', label: 'Message Parents & Staff', desc: 'Send messages to parents and other staff' },
                              { key: 'can_message_staff_only', label: 'Message Staff Only', desc: 'Send messages to administrators/directors only' },
                            ].map((perm) => (
                              <div key={perm.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                <div>
                                  <p className="font-medium text-sm">{perm.label}</p>
                                  <p className="text-xs text-muted-foreground">{perm.desc}</p>
                                </div>
                                <Switch
                                  checked={permissions[perm.key]}
                                  onCheckedChange={(checked) => setPermissions({...permissions, [perm.key]: checked})}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Reports */}
                        <div>
                          <h4 className="font-semibold text-sm text-muted-foreground mb-2">📝 Reports</h4>
                          <div className="space-y-2">
                            {[
                              { key: 'can_create_reports', label: 'Create Reports', desc: 'Write daily activity reports' },
                            ].map((perm) => (
                              <div key={perm.key} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                                <div>
                                  <p className="font-medium text-sm">{perm.label}</p>
                                  <p className="text-xs text-muted-foreground">{perm.desc}</p>
                                </div>
                                <Switch
                                  checked={permissions[perm.key]}
                                  onCheckedChange={(checked) => setPermissions({...permissions, [perm.key]: checked})}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </ScrollArea>
                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button variant="outline" onClick={() => setPermissionsDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleSavePermissions}>
                        Save Permissions
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="space-y-6">
              {/* Application Links */}
              <Card className="card-clay">
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Link className="w-5 h-5 text-primary" />
                    Application Links
                  </CardTitle>
                  <CardDescription>Share these links for new employee and child enrollment applications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted/50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-primary" />
                        <span className="font-semibold">Employee Application</span>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(employeeAppLink, 'employee')}
                        >
                          {copiedLink === 'employee' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="outline" asChild>
                          <a href={employeeAppLink} target="_blank" rel="noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                    <code className="text-sm text-muted-foreground break-all">{employeeAppLink}</code>
                  </div>

                  <div className="p-4 bg-muted/50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Baby className="w-5 h-5 text-primary" />
                        <span className="font-semibold">Child Enrollment Application</span>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyToClipboard(childAppLink, 'child')}
                        >
                          {copiedLink === 'child' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                        <Button size="sm" variant="outline" asChild>
                          <a href={childAppLink} target="_blank" rel="noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      </div>
                    </div>
                    <code className="text-sm text-muted-foreground break-all">{childAppLink}</code>
                  </div>
                </CardContent>
              </Card>

              {/* Received Applications */}
              <Card className="card-clay">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Received Applications</CardTitle>
                    <CardDescription>Review and manage incoming applications</CardDescription>
                  </div>
                  {applications.length > 0 && (
                    <Button 
                      variant="outline" 
                      onClick={downloadAllApplicationsPDF}
                      data-testid="download-all-applications-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download All PDFs
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {applications.length > 0 ? (
                    <div className="space-y-4">
                      {applications.map((app) => (
                        <div 
                          key={app.id}
                          className="p-4 bg-muted/50 rounded-xl"
                          data-testid={`application-${app.id}`}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                {app.type === 'employee' ? (
                                  <Badge className="bg-blue-100 text-blue-800">Employee</Badge>
                                ) : (
                                  <Badge className="bg-green-100 text-green-800">Child Enrollment</Badge>
                                )}
                                <Badge variant={app.status === 'pending' ? 'secondary' : app.status === 'approved' ? 'default' : 'destructive'}>
                                  {app.status}
                                </Badge>
                                {app.type === 'child' && app.children && (
                                  <Badge variant="outline">{app.children.length} child{app.children.length > 1 ? 'ren' : ''}</Badge>
                                )}
                              </div>
                              <h4 className="font-semibold">
                                {app.type === 'employee' 
                                  ? app.name 
                                  : app.children 
                                    ? app.children.map(c => `${c.first_name} ${c.last_name}`).join(', ')
                                    : `${app.child_first_name} ${app.child_last_name}`
                                }
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {app.type === 'employee' ? app.email : (app.parent1_email || app.parent_email)} • {new Date(app.submitted_at).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => viewApplicationDetails(app.id)}
                                data-testid={`view-app-${app.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => downloadApplicationPDF(
                                  app.id, 
                                  app.type === 'employee' 
                                    ? app.name 
                                    : app.children 
                                      ? app.children[0]?.first_name 
                                      : app.child_first_name
                                )}
                                data-testid={`download-app-${app.id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                PDF
                              </Button>
                              {app.status === 'pending' && (
                                <>
                                  <Button size="sm" onClick={() => updateApplicationStatus(app.id, 'approved')}>
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      const note = window.prompt('Optional note (why are you deferring this?)');
                                      if (note !== null) updateApplicationStatus(app.id, 'under_review', note || '');
                                    }}
                                    data-testid={`follow-up-app-${app.id}`}
                                  >
                                    <Clock className="w-4 h-4 mr-1" />
                                    Follow Up Later
                                  </Button>
                                  {app.type === 'child' && (
                                    <Button 
                                      size="sm" 
                                      variant="outline"
                                      onClick={() => addToWaitlist(app.id)}
                                      data-testid={`waitlist-app-${app.id}`}
                                    >
                                      <Clock className="w-4 h-4 mr-1" />
                                      Waitlist
                                    </Button>
                                  )}
                                  <Button size="sm" variant="outline" onClick={() => updateApplicationStatus(app.id, 'rejected')}>
                                    Reject
                                  </Button>
                                </>
                              )}
                              {app.status === 'under_review' && (
                                <>
                                  <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200">
                                    <Clock className="w-3 h-3 mr-1" /> Under Review
                                  </Badge>
                                  <Button size="sm" onClick={() => updateApplicationStatus(app.id, 'approved')}>
                                    Approve
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => updateApplicationStatus(app.id, 'rejected')}>
                                    Reject
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      const note = window.prompt('Add a follow-up note (optional)');
                                      if (note !== null) updateApplicationStatus(app.id, 'under_review', note || '');
                                    }}
                                  >
                                    Add note
                                  </Button>
                                </>
                              )}
                              {app.status === 'waitlisted' && (
                                <Badge className="bg-yellow-100 text-yellow-800">On Waitlist</Badge>
                              )}
                            </div>
                          </div>
                          {app.type === 'employee' && (
                            <p className="text-sm text-muted-foreground">
                              Position: {app.position} • Experience: {app.experience_years} years
                            </p>
                          )}
                          {Array.isArray(app.review_notes) && app.review_notes.length > 0 && (
                            <div className="mt-3 border-t border-border/60 pt-2 space-y-1">
                              <div className="text-xs font-semibold text-muted-foreground">Review notes</div>
                              {app.review_notes.slice(-3).map((n, i) => (
                                <div key={i} className="text-xs text-muted-foreground">
                                  <span className="font-medium text-foreground">{n.author}</span> · {new Date(n.at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                  {n.status && <> · <span className="italic">{n.status}</span></>}
                                  {n.note && <>: {n.note}</>}
                                </div>
                              ))}
                            </div>
                          )}
                          {app.type === 'child' && (
                            <p className="text-sm text-muted-foreground">
                              Parent: {app.parent1_name || app.parent_name} • Preferred Start: {app.preferred_start_date || 'Not specified'}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No applications received yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Application Detail Dialog */}
              <Dialog open={applicationDetailOpen} onOpenChange={setApplicationDetailOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                  <DialogHeader>
                    <DialogTitle className="font-heading flex items-center gap-2">
                      <FileText className="w-5 h-5 text-primary" />
                      Application Details
                    </DialogTitle>
                  </DialogHeader>
                  {selectedApplication && (
                    <ScrollArea className="flex-1 pr-4">
                      <div className="space-y-6 py-4">
                        {/* Status and Actions */}
                        <div className="flex items-center justify-between p-4 bg-muted rounded-xl">
                          <div className="flex items-center gap-3">
                            <Badge className={selectedApplication.type === 'employee' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                              {selectedApplication.type === 'employee' ? 'Employee Application' : 'Child Enrollment'}
                            </Badge>
                            <Badge variant={selectedApplication.status === 'pending' ? 'secondary' : selectedApplication.status === 'approved' ? 'default' : 'destructive'}>
                              {selectedApplication.status}
                            </Badge>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            Submitted: {new Date(selectedApplication.submitted_at).toLocaleString()}
                          </span>
                        </div>

                        {/* Employee Application Details */}
                        {selectedApplication.type === 'employee' && (
                          <div className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                              <div className="space-y-4">
                                <h4 className="font-semibold border-b pb-2">Personal Information</h4>
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <Users className="w-4 h-4 text-muted-foreground" />
                                    <span className="font-medium">{selectedApplication.name}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-muted-foreground" />
                                    <span>{selectedApplication.email}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-muted-foreground" />
                                    <span>{selectedApplication.phone}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-4">
                                <h4 className="font-semibold border-b pb-2">Position Details</h4>
                                <div className="space-y-2">
                                  <p><strong>Position:</strong> {selectedApplication.position}</p>
                                  <p><strong>Experience:</strong> {selectedApplication.experience_years} years</p>
                                  <p><strong>Education:</strong> {selectedApplication.education || 'Not specified'}</p>
                                  <p><strong>Availability:</strong> {selectedApplication.availability || 'Not specified'}</p>
                                </div>
                              </div>
                            </div>
                            {selectedApplication.certifications && (
                              <div>
                                <h4 className="font-semibold border-b pb-2 mb-2">Certifications</h4>
                                <p>{selectedApplication.certifications}</p>
                              </div>
                            )}
                            {selectedApplication.references && (
                              <div>
                                <h4 className="font-semibold border-b pb-2 mb-2">References</h4>
                                <p className="whitespace-pre-wrap">{selectedApplication.references}</p>
                              </div>
                            )}
                            {selectedApplication.cover_letter && (
                              <div>
                                <h4 className="font-semibold border-b pb-2 mb-2">Cover Letter</h4>
                                <p className="whitespace-pre-wrap">{selectedApplication.cover_letter}</p>
                              </div>
                            )}
                            
                            {/* Uploaded Documents */}
                            {(selectedApplication.resume_filename || (selectedApplication.certification_files && selectedApplication.certification_files.length > 0)) && (
                              <div className="p-4 bg-blue-50 rounded-xl space-y-4">
                                <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                                  <FileText className="w-4 h-4" />
                                  Uploaded Documents
                                </h4>
                                
                                {selectedApplication.resume_filename && (
                                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                                    <div className="flex items-center gap-3">
                                      <FileText className="w-5 h-5 text-red-500" />
                                      <div>
                                        <p className="font-medium">Resume</p>
                                        <p className="text-sm text-muted-foreground">{selectedApplication.resume_filename}</p>
                                      </div>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => downloadApplicationFile(selectedApplication.id, 'resume')}
                                      data-testid={`download-resume-${selectedApplication.id}`}
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      Download
                                    </Button>
                                  </div>
                                )}
                                
                                {selectedApplication.certification_files && selectedApplication.certification_files.length > 0 && (
                                  <div className="space-y-2">
                                    <p className="text-sm font-medium text-blue-700">Certification Documents ({selectedApplication.certification_files.length})</p>
                                    {selectedApplication.certification_files.map((file, index) => (
                                      <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg">
                                        <div className="flex items-center gap-3">
                                          <FileText className="w-5 h-5 text-blue-500" />
                                          <span className="text-sm font-medium">{file.filename}</span>
                                        </div>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => downloadApplicationFile(selectedApplication.id, 'certification', index)}
                                          data-testid={`download-cert-${selectedApplication.id}-${index}`}
                                        >
                                          <Download className="w-4 h-4 mr-1" />
                                          Download
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Child Enrollment Application Details */}
                        {selectedApplication.type === 'child' && (
                          <div className="space-y-6">
                            {/* Children Info */}
                            {selectedApplication.children && selectedApplication.children.length > 0 && (
                              <Accordion type="multiple" defaultValue={['child-0']} className="space-y-2">
                                {selectedApplication.children.map((child, index) => (
                                  <AccordionItem key={index} value={`child-${index}`} className="border rounded-xl px-4">
                                    <AccordionTrigger className="hover:no-underline">
                                      <div className="flex items-center gap-3">
                                        <Baby className="w-5 h-5 text-primary" />
                                        <span className="font-semibold">{child.first_name} {child.last_name}</span>
                                        <Badge variant="outline">{child.date_of_birth}</Badge>
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="space-y-4 pt-4">
                                      <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                          <p className="text-sm text-muted-foreground">Gender</p>
                                          <p className="font-medium capitalize">{child.gender || 'Not specified'}</p>
                                        </div>
                                      </div>
                                      
                                      {/* Health Information */}
                                      <div className="p-4 bg-red-50 rounded-lg space-y-3">
                                        <h5 className="font-semibold text-red-800 flex items-center gap-2">
                                          <AlertTriangle className="w-4 h-4" />
                                          Health Information
                                        </h5>
                                        {child.allergies && child.allergies !== 'none' && (
                                          <div>
                                            <p className="text-sm font-medium text-red-700">Allergies: {child.allergies}</p>
                                            {child.allergies_details && (
                                              <p className="text-sm text-red-600">{child.allergies_details}</p>
                                            )}
                                          </div>
                                        )}
                                        {child.current_medications && (
                                          <p className="text-sm"><strong>Medications:</strong> {child.current_medications}</p>
                                        )}
                                        {child.medical_conditions && (
                                          <p className="text-sm"><strong>Medical Conditions:</strong> {child.medical_conditions}</p>
                                        )}
                                        {child.dietary_restrictions && (
                                          <p className="text-sm"><strong>Dietary Restrictions:</strong> {child.dietary_restrictions}</p>
                                        )}
                                      </div>

                                      {/* Physician Info */}
                                      <div className="grid md:grid-cols-2 gap-4">
                                        <div>
                                          <p className="text-sm text-muted-foreground">Physician</p>
                                          <p className="font-medium">{child.physician_name || 'Not specified'}</p>
                                          <p className="text-sm">{child.physician_phone}</p>
                                        </div>
                                        <div>
                                          <p className="text-sm text-muted-foreground">Health Insurance</p>
                                          <p className="font-medium">{child.health_insurance || 'Not specified'}</p>
                                          <p className="text-sm">{child.health_insurance_number}</p>
                                        </div>
                                      </div>

                                      {/* Infant/Toddler Info */}
                                      {(child.feeding_schedule || child.sleep_schedule) && (
                                        <div className="p-4 bg-amber-50 rounded-lg space-y-2">
                                          <h5 className="font-semibold text-amber-800">Infant/Toddler Care</h5>
                                          {child.feeding_schedule && <p className="text-sm"><strong>Feeding:</strong> {child.feeding_schedule}</p>}
                                          {child.formula_type && <p className="text-sm"><strong>Formula:</strong> {child.formula_type}</p>}
                                          {child.sleep_schedule && <p className="text-sm"><strong>Sleep:</strong> {child.sleep_schedule}</p>}
                                          {child.comfort_items && <p className="text-sm"><strong>Comfort Items:</strong> {child.comfort_items}</p>}
                                        </div>
                                      )}

                                      {/* Authorizations */}
                                      <div className="flex gap-4">
                                        <Badge variant={child.authorized_for_emergency_care ? 'default' : 'secondary'}>
                                          Emergency Care: {child.authorized_for_emergency_care ? 'Yes' : 'No'}
                                        </Badge>
                                        <Badge variant={child.authorized_for_field_trips ? 'default' : 'secondary'}>
                                          Field Trips: {child.authorized_for_field_trips ? 'Yes' : 'No'}
                                        </Badge>
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                ))}
                              </Accordion>
                            )}

                            {/* Legacy single child format */}
                            {!selectedApplication.children && selectedApplication.child_first_name && (
                              <div className="p-4 border rounded-xl">
                                <h4 className="font-semibold mb-3">Child Information</h4>
                                <p><strong>Name:</strong> {selectedApplication.child_first_name} {selectedApplication.child_last_name}</p>
                                <p><strong>DOB:</strong> {selectedApplication.child_dob}</p>
                                {selectedApplication.allergies && <p><strong>Allergies:</strong> {selectedApplication.allergies}</p>}
                                {selectedApplication.medical_notes && <p><strong>Medical Notes:</strong> {selectedApplication.medical_notes}</p>}
                              </div>
                            )}

                            {/* Parent Information */}
                            <div className="grid md:grid-cols-2 gap-6">
                              <div className="p-4 border rounded-xl">
                                <h4 className="font-semibold mb-3 flex items-center gap-2">
                                  <Users className="w-4 h-4 text-primary" />
                                  Parent/Guardian 1
                                </h4>
                                <div className="space-y-2 text-sm">
                                  <p className="font-medium">{selectedApplication.parent1_name || selectedApplication.parent_name}</p>
                                  <div className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-muted-foreground" />
                                    {selectedApplication.parent1_email || selectedApplication.parent_email}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-muted-foreground" />
                                    {selectedApplication.parent1_phone || selectedApplication.parent_phone}
                                  </div>
                                  {selectedApplication.parent1_employer && (
                                    <div className="flex items-center gap-2">
                                      <Briefcase className="w-4 h-4 text-muted-foreground" />
                                      {selectedApplication.parent1_employer}
                                    </div>
                                  )}
                                  {selectedApplication.parent1_address && (
                                    <div className="flex items-start gap-2">
                                      <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                                      {selectedApplication.parent1_address}
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {selectedApplication.parent2_name && (
                                <div className="p-4 border rounded-xl">
                                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                                    <Users className="w-4 h-4 text-primary" />
                                    Parent/Guardian 2
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    <p className="font-medium">{selectedApplication.parent2_name}</p>
                                    <div className="flex items-center gap-2">
                                      <Phone className="w-4 h-4 text-muted-foreground" />
                                      {selectedApplication.parent2_phone}
                                    </div>
                                    {selectedApplication.parent2_employer && (
                                      <div className="flex items-center gap-2">
                                        <Briefcase className="w-4 h-4 text-muted-foreground" />
                                        {selectedApplication.parent2_employer}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Emergency Contacts */}
                            {selectedApplication.emergency_contacts && selectedApplication.emergency_contacts.length > 0 && (
                              <div className="p-4 border rounded-xl">
                                <h4 className="font-semibold mb-3">Emergency Contacts</h4>
                                <div className="grid md:grid-cols-2 gap-4">
                                  {selectedApplication.emergency_contacts.map((contact, idx) => (
                                    contact.name && (
                                      <div key={idx} className="text-sm">
                                        <p className="font-medium">{contact.name}</p>
                                        <p className="text-muted-foreground">{contact.relationship}</p>
                                        <p>{contact.phone}</p>
                                      </div>
                                    )
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Authorized Pickup */}
                            {selectedApplication.authorized_pickup && selectedApplication.authorized_pickup.length > 0 && (
                              <div className="p-4 border rounded-xl">
                                <h4 className="font-semibold mb-3">Authorized Pickup</h4>
                                <div className="grid md:grid-cols-3 gap-4">
                                  {selectedApplication.authorized_pickup.map((person, idx) => (
                                    person.name && (
                                      <div key={idx} className="text-sm">
                                        <p className="font-medium">{person.name}</p>
                                        <p className="text-muted-foreground">{person.relationship}</p>
                                        <p>{person.phone}</p>
                                      </div>
                                    )
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Consents */}
                            <div className="p-4 border rounded-xl">
                              <h4 className="font-semibold mb-3">Consents & Authorizations</h4>
                              <div className="flex flex-wrap gap-2">
                                <Badge variant={selectedApplication.photo_consent ? 'default' : 'secondary'}>
                                  Photo: {selectedApplication.photo_consent ? 'Yes' : 'No'}
                                </Badge>
                                <Badge variant={selectedApplication.field_trip_consent ? 'default' : 'secondary'}>
                                  Field Trips: {selectedApplication.field_trip_consent ? 'Yes' : 'No'}
                                </Badge>
                                <Badge variant={selectedApplication.medical_consent ? 'default' : 'secondary'}>
                                  Medical: {selectedApplication.medical_consent ? 'Yes' : 'No'}
                                </Badge>
                                <Badge variant={selectedApplication.sunscreen_consent ? 'default' : 'secondary'}>
                                  Sunscreen: {selectedApplication.sunscreen_consent ? 'Yes' : 'No'}
                                </Badge>
                              </div>
                              {selectedApplication.signature && (
                                <div className="mt-4 pt-4 border-t">
                                  <p className="text-sm text-muted-foreground">Electronic Signature</p>
                                  <p className="font-accent text-xl">{selectedApplication.signature}</p>
                                  <p className="text-sm text-muted-foreground">{selectedApplication.signature_date}</p>
                                </div>
                              )}
                            </div>

                            {/* Schedule */}
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm text-muted-foreground">Preferred Start Date</p>
                                <p className="font-medium">{selectedApplication.preferred_start_date || 'Not specified'}</p>
                              </div>
                              <div>
                                <p className="text-sm text-muted-foreground">Preferred Schedule</p>
                                <p className="font-medium">{selectedApplication.preferred_schedule || 'Not specified'}</p>
                              </div>
                            </div>

                            {selectedApplication.additional_notes && (
                              <div>
                                <h4 className="font-semibold border-b pb-2 mb-2">Additional Notes</h4>
                                <p className="whitespace-pre-wrap">{selectedApplication.additional_notes}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-between items-center pt-4 border-t">
                          <Button 
                            variant="outline"
                            onClick={() => downloadApplicationPDF(
                              selectedApplication.id,
                              selectedApplication.type === 'employee'
                                ? selectedApplication.name
                                : selectedApplication.children?.[0]?.first_name || selectedApplication.child_first_name
                            )}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download PDF
                          </Button>
                          {selectedApplication.status === 'pending' && (
                            <div className="flex gap-3">
                              <Button variant="outline" onClick={() => {
                                updateApplicationStatus(selectedApplication.id, 'rejected');
                                setApplicationDetailOpen(false);
                              }}>
                                Reject Application
                              </Button>
                              <Button onClick={() => {
                                updateApplicationStatus(selectedApplication.id, 'approved');
                                setApplicationDetailOpen(false);
                              }}>
                                Approve Application
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </ScrollArea>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>
          
          {/* Reminders Settings Tab */}
          <TabsContent value="reminders">
            <RemindersSettings getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <PaymentSettings getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="email-templates">
            <EmailTemplatesSettings getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* Newsletter Poster Settings Tab */}
          <TabsContent value="newsletter">
            <NewsletterPosterSettings />
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources">
            <Card className="card-clay">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Platform Resources
                </CardTitle>
                <CardDescription>Download helpful guides and documentation</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Demo Sheet */}
                <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold flex items-center gap-2">
                        <FileText className="w-4 h-4 text-indigo-600" />
                        Platform Overview / Demo Sheet
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        A comprehensive PDF highlighting all features of CareKids WI - perfect for training new staff or sharing with parents.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">DCF Forms</span>
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Child Management</span>
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Billing</span>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Parent Portal</span>
                      </div>
                    </div>
                    <Button 
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/demo-sheet/pdf`, {
                            ...getAuthHeaders(),
                            responseType: 'blob'
                          });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', 'CareKidsWI_Platform_Overview.pdf');
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          toast.success('Demo sheet downloaded!');
                        } catch (error) {
                          toast.error('Failed to download demo sheet');
                        }
                      }}
                      className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>

                {/* DCF Resources Link */}
                <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Globe className="w-4 h-4 text-blue-600" />
                        Wisconsin DCF Resources
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Official Wisconsin Department of Children and Families website with licensing requirements and regulations.
                      </p>
                    </div>
                    <Button 
                      variant="outline"
                      onClick={() => window.open('https://dcf.wisconsin.gov/childcare', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Visit DCF
                    </Button>
                  </div>
                </div>

                {/* Contact Support */}
                <div className="p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold flex items-center gap-2">
                        <HelpCircle className="w-4 h-4 text-green-600" />
                        Need Help?
                      </h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Contact our support team for assistance with the platform.
                      </p>
                    </div>
                    <Button 
                      variant="outline"
                      onClick={() => window.location.href = 'mailto:info@carekidswi.com'}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Support
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-primary" />
                  Center Documents
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Upload and manage center-level documents: DCF license, fire inspection, insurance, policies, and more.
                </p>
              </CardHeader>
              <CardContent>
                <CenterDocuments getAuthHeaders={getAuthHeaders} centerId={user?.center_id || user?.id} />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Agreement Defaults Tab (DCF-F-CFS5224) */}
          <TabsContent value="payment-agreement">
            <PaymentAgreementDefaultsCard getAuthHeaders={getAuthHeaders} />
          </TabsContent>

          {/* Transport Settings Tab (speed limit + ping interval) */}
          <TabsContent value="transport">
            <TransportSettingsCard getAuthHeaders={getAuthHeaders} />
          </TabsContent>
        </Tabs>
      </div>

      {/* PDF Download Dialog */}
      <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5 text-primary" />
              Download Staff Checklist PDF
            </DialogTitle>
            <DialogDescription>
              Select staff members to include in the PDF report
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Date Range */}
            <div className="p-3 bg-muted/50 rounded-lg">
              <label className="text-sm font-medium mb-2 block">Report Date Range (Optional)</label>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={checklistDateRange.start}
                  onChange={(e) => setChecklistDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="flex-1"
                  placeholder="Start Date"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={checklistDateRange.end}
                  onChange={(e) => setChecklistDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="flex-1"
                  placeholder="End Date"
                />
              </div>
            </div>

            {/* Staff Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Select Staff Members</label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={selectAllForPDF} className="h-7 text-xs">
                    Select All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={deselectAllForPDF} className="h-7 text-xs">
                    Clear
                  </Button>
                </div>
              </div>
              <ScrollArea className="h-[250px] border rounded-lg p-2">
                <div className="space-y-1">
                  {staff.map(member => {
                    const checklist = staffChecklistData[member.id] || {};
                    const completedCount = dcfChecklistItems.filter(item => checklist[item.id] === 'X').length;
                    const isSelected = selectedStaffForPDF.includes(member.id);
                    
                    return (
                      <div 
                        key={member.id}
                        className={`flex items-center justify-between p-2 rounded cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-primary/10 border border-primary/30' : ''}`}
                        onClick={() => toggleStaffForPDF(member.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox 
                            checked={isSelected}
                            onCheckedChange={() => toggleStaffForPDF(member.id)}
                          />
                          <div>
                            <p className="font-medium text-sm">{member.user_name}</p>
                            <p className="text-xs text-muted-foreground">{member.position}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            completedCount === dcfChecklistItems.length 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {completedCount}/{dcfChecklistItems.length}
                          </span>
                          {member.dcf_compliant && (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
              <p className="text-xs text-muted-foreground mt-2">
                {selectedStaffForPDF.length} of {staff.length} staff selected
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={downloadStaffChecklistPDF} disabled={selectedStaffForPDF.length === 0}>
              <Download className="w-4 h-4 mr-2" />
              Download PDF ({selectedStaffForPDF.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};


// ==================== PAYMENT AGREEMENT DEFAULTS (DCF-F-CFS5224) ====================

const PA_FIELDS = [
  { key: 'days_hours_of_operation', label: 'Days & hours of operation', hint: 'e.g., Monday–Friday 6:30 AM – 6:00 PM' },
  { key: 'deposit_policy', label: 'Deposits / holding a slot', hint: 'Amount, when due, refundable or not' },
  { key: 'closure_dates_policy', label: 'Anticipated closure dates & payment policy during closures', hint: 'Holidays, staff training days, tuition impact' },
  { key: 'expected_absence_policy', label: 'Expected child absences policy', hint: 'Vacations, planned trips — is tuition still owed?' },
  { key: 'unexpected_absence_policy', label: 'Unexpected child absences policy', hint: 'Sickness, emergencies — is tuition still owed?' },
  { key: 'payment_dispute_policy', label: 'Payment dispute policy', hint: 'How disputes are handled' },
  { key: 'termination_policy', label: 'Reasons & procedures for termination / expulsion', hint: 'Notice periods, causes' },
  { key: 'available_discounts', label: 'Discounts & scholarships offered', hint: 'Sibling discount, subsidy, etc.' },
  { key: 'provider_fees', label: 'Additional provider fees', hint: 'Field trips, late pickup, activity fees' },
  { key: 'misc_policies', label: 'Miscellaneous policies', hint: 'Anticipated schedule, drop-off/pickup times, other rules' },
  { key: 'provider_contact_name', label: 'Provider contact name (for the signature block)', hint: 'Full name of the person who signs on behalf of the center' },
];

const PaymentAgreementDefaultsCard = ({ getAuthHeaders }) => {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/payment-agreement-defaults`, getAuthHeaders());
        setValues(r.data || {});
      } catch (e) {
        // fall back to empty form
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setValues(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/payment-agreement-defaults`, values, getAuthHeaders());
      toast.success('Payment agreement defaults saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <Card className="card-clay">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-primary" />
          Provider / Parent Payment Agreement — Center Defaults
        </CardTitle>
        <CardDescription>
          These answers auto-populate the <strong>DCF-F-CFS5224</strong> agreement parents review and sign
          during enrollment. Answer once; every new enrollment link will use these.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {PA_FIELDS.map(f => (
          <div key={f.key} data-testid={`pa-field-${f.key}`}>
            <Label className="font-semibold">{f.label}</Label>
            {f.hint && <p className="text-xs text-muted-foreground mb-1">{f.hint}</p>}
            {f.key === 'provider_contact_name' || f.key === 'days_hours_of_operation' ? (
              <Input value={values[f.key] || ''} onChange={e => set(f.key, e.target.value)} />
            ) : (
              <Textarea rows={3} value={values[f.key] || ''} onChange={e => set(f.key, e.target.value)} />
            )}
          </div>
        ))}
        <div className="flex justify-end pt-2 border-t">
          <Button onClick={save} disabled={saving} data-testid="pa-save">
            {saving ? 'Saving…' : 'Save defaults'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


// ==================== TRANSPORT SETTINGS (Driver speed) ====================

const TransportSettingsCard = ({ getAuthHeaders }) => {
  const [values, setValues] = useState({
    speed_limit_mph: 55,
    ping_interval_sec: 10,
    speedometer_visible: true,
    hard_brake_threshold_g: 0.45,
    harsh_turn_threshold_g: 0.40,
    motion_detection_enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/transport/settings`, getAuthHeaders());
        setValues(r.data);
      } catch (e) {
        // keep defaults
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/transport/settings`, values, getAuthHeaders());
      setValues(r.data);
      toast.success('Transport settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <Card className="card-clay">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-primary" />
          Transport / Driver Speed Settings
        </CardTitle>
        <CardDescription>
          Set the speed limit and GPS ping cadence for your van/bus drivers. Over-limit speeds are
          logged automatically and shown on the admin dashboard.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="font-semibold">Speed limit (mph)</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Drivers' speedometer turns red + an alert is logged when they exceed this.
            </p>
            <Input
              type="number"
              min="10"
              max="120"
              value={values.speed_limit_mph}
              onChange={(e) => setValues(v => ({ ...v, speed_limit_mph: Number(e.target.value) }))}
              data-testid="ts-speed-limit"
            />
            <div className="flex gap-1 mt-2">
              {[25, 35, 45, 55, 65].map(p => (
                <Button
                  key={p}
                  type="button"
                  size="sm"
                  variant={values.speed_limit_mph === p ? 'default' : 'outline'}
                  className="h-7 px-2 text-xs"
                  onClick={() => setValues(v => ({ ...v, speed_limit_mph: p }))}
                >
                  {p}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="font-semibold">Location ping interval (seconds)</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Lower = more accurate route trace, more data. 10 is a good default.
            </p>
            <Input
              type="number"
              min="5"
              max="300"
              value={values.ping_interval_sec}
              onChange={(e) => setValues(v => ({ ...v, ping_interval_sec: Number(e.target.value) }))}
              data-testid="ts-ping-interval"
            />
            <div className="flex gap-1 mt-2">
              {[5, 10, 15, 30, 60].map(p => (
                <Button
                  key={p}
                  type="button"
                  size="sm"
                  variant={values.ping_interval_sec === p ? 'default' : 'outline'}
                  className="h-7 px-2 text-xs"
                  onClick={() => setValues(v => ({ ...v, ping_interval_sec: p }))}
                >
                  {p}s
                </Button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <Switch
            checked={values.speedometer_visible}
            onCheckedChange={(v) => setValues(prev => ({ ...prev, speedometer_visible: v }))}
            data-testid="ts-speedometer-visible"
          />
          <div>
            <Label className="font-semibold">Show live speedometer to drivers</Label>
            <p className="text-xs text-muted-foreground">
              If off, speed is tracked silently in the background without being shown on their screen.
            </p>
          </div>
        </div>

        {/* Harsh motion detection */}
        <div className="pt-4 border-t">
          <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg mb-3">
            <Switch
              checked={values.motion_detection_enabled}
              onCheckedChange={(v) => setValues(prev => ({ ...prev, motion_detection_enabled: v }))}
              data-testid="ts-motion-enabled"
            />
            <div>
              <Label className="font-semibold">Detect hard brakes and harsh turns</Label>
              <p className="text-xs text-muted-foreground">
                Uses the device accelerometer. iOS tablets will prompt the driver for permission once.
              </p>
            </div>
          </div>

          {values.motion_detection_enabled && (
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="font-semibold">Hard-brake threshold (g)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Logs a hard-brake event when forward deceleration exceeds this. 0.45 g ≈ hitting the brakes firmly.
                </p>
                <Input
                  type="number"
                  min="0.2"
                  max="1.5"
                  step="0.05"
                  value={values.hard_brake_threshold_g}
                  onChange={(e) => setValues(v => ({ ...v, hard_brake_threshold_g: Number(e.target.value) }))}
                  data-testid="ts-hard-brake"
                />
                <div className="flex gap-1 mt-2">
                  {[0.30, 0.40, 0.45, 0.55, 0.70].map(p => (
                    <Button
                      key={p}
                      type="button"
                      size="sm"
                      variant={values.hard_brake_threshold_g === p ? 'default' : 'outline'}
                      className="h-7 px-2 text-xs"
                      onClick={() => setValues(v => ({ ...v, hard_brake_threshold_g: p }))}
                    >
                      {p}g
                    </Button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-semibold">Harsh-turn threshold (g)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Logs a harsh-turn event when lateral acceleration exceeds this. 0.40 g ≈ a sharp swerve.
                </p>
                <Input
                  type="number"
                  min="0.2"
                  max="1.5"
                  step="0.05"
                  value={values.harsh_turn_threshold_g}
                  onChange={(e) => setValues(v => ({ ...v, harsh_turn_threshold_g: Number(e.target.value) }))}
                  data-testid="ts-harsh-turn"
                />
                <div className="flex gap-1 mt-2">
                  {[0.25, 0.35, 0.40, 0.50, 0.65].map(p => (
                    <Button
                      key={p}
                      type="button"
                      size="sm"
                      variant={values.harsh_turn_threshold_g === p ? 'default' : 'outline'}
                      className="h-7 px-2 text-xs"
                      onClick={() => setValues(v => ({ ...v, harsh_turn_threshold_g: p }))}
                    >
                      {p}g
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-2 border-t">
          <Button onClick={save} disabled={saving} data-testid="ts-save">
            {saving ? 'Saving…' : 'Save transport settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};


export default SettingsPage;